import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, FormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PurchasingService } from '../../services/purchasing.service';
import { OmsService } from '../../services/oms.service';
import { VendorService } from '../../services/vendor.service';
import { OrdersService } from '../../services/orders.service';
import { BarcodeScannerLivestreamComponent } from 'ngx-barcode-scanner';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-warehouse-item-scan-lite',
	templateUrl: './warehouse-item-scan-lite.component.html',
	styleUrls: ['./warehouse-item-scan-lite.component.scss']
})
export class WarehouseItemScanLiteComponent implements OnInit, OnDestroy {

	@ViewChild('hiddenScanInput') hiddenScanInput: ElementRef;
	@ViewChild('barcodeScanner') barcodeScanner: BarcodeScannerLivestreamComponent;

	// Constants
	public readonly MST_ITEM_TYPE = 'MST';

	// Core properties
	currentItem: any = null;
	scanMode: 'single' | 'bulk' = 'single';
	bulkQuantity = 0; // Quantity to record for bulk mode
	bulkQuantityInput = 0; // Input field for quantity
	isScanning = false;
	lastScanTime = 0;
	private timeUpdateInterval: any;
	lastSearchTerm: any = '';
	// Essential properties from warehouse-scaninout
	CHAT_ROOM = 'OrderBoard';
	qtyBin: UntypedFormControl = new UntypedFormControl({ value: 'RECDOC', disabled: true }, Validators.required);

	// Scan detail modal properties
	showScanDetailModal = false;
	selectedScanDetail: any = null;

	// Form controls
	scanForm: UntypedFormGroup;
	hiddenScanControl: UntypedFormControl = new UntypedFormControl('');
	qtyRec: UntypedFormControl = new UntypedFormControl(0, Validators.required);

	// Visual feedback
	scanStatus: 'idle' | 'success' | 'error' | 'warning' | 'processing' = 'idle';
	statusMessage = '';
	binLocation = '';

	// Audio and vibration
	color: any = '';

	// Barcode scanner
	useCameraScanner = false;


	// Search functionality
	searchInput = '';
	searchResults: any[] = [];
	isSearchMode = false;
	showSearchResults = false;
	isMobile = false;

	// Add barcode functionality
	showBarcodeAddModal = false;
	showBarcodeAddForm = false;
	barcodeAddError = '';
	private originalMode: 'scan' | 'search' | null = null;

	// Bottom menu visibility control
	get shouldShowBottomMenu(): boolean {
		return !this.showBarcodeAddModal;
	}


	// Touch gesture support for bottom sheet
	private touchStartY = 0;
	private touchCurrentY = 0;
	private isDragging = false;

	// Keyboard listener control
	keyboardListenerEnabled = true;

	// Manager mode state memory
	private modeBeforeManager = false;
	// Scan-mode memory for send/receive toggle — restore on flip back to scan-in
	private modeBeforeSendOut: 'single' | 'bulk' = 'single';
	private searchModeBeforeSendOut = false;

	// MST modal
	showMstModal = false;
	mstList: any[] = [];
	loadingMstData = false;
	mstToggle: FormControl = new FormControl(false); // false = receive, true = send

	// Advanced controls drawer
	showAdvancedDrawer = false;

	// Vendor PO display
	showVendorPoTable = false;
	vendorPoDetails: any[] = [];

	// Warehouse properties (from warehouse-scaninout)
	receiving_quantity = 0;
	receiving_bin = 'RECDOC';
	expected: any = false;
	already_scanned: any = 0;
	scan_history: any = false;
	private hadScansBeforeRefresh = false;
	token: any = false;
	user: any = [];
	location: any = '';

	// Core receiving functionality
	vendor_selected = 'DEFAULT';
	qtyAdded = false;
	showScanHistory = false;

	// Vendor management
	selectedVendor: any = null; // No default vendor - user must select
	availableVendors: any[] = [];
	showVendorButtons = false;

	// Purchase order integration
	purchaseOrders: any = null;
	expectedQuantity = 0;
	loadingPurchaseOrders = false;

	// PO Selection feature
	enablePoSelection = true; // Feature flag (defaults to on - requested)
	showPoSelectionModal = false; // Controls modal visibility
	selectedPoDetails: any = null; // Selected PO (similar to mstdetails)
	availablePos: any[] = []; // List of POs to select from

	// Send/Receive toggle and RGA support
	sendToggle: UntypedFormControl = new UntypedFormControl(false);
	vendorRgas: any = null;

	// RGA creation tracking
	itemRgaMap: { [stockid: string]: string } = {}; // Maps stockid to RGA orderno (kept for backward compatibility)
	vendorRgaMap: { [vendor: string]: string } = {}; // Maps vendor to current open RGA orderno

	// Item details for warehouse-scaninout component
	itemDetailsForScaninout: any = null;

	// MST support (handled by warehouse-scaninout component)
	mstdetails: any = null;

	// UI state
	showDetails = false; // Hidden by default
	undoingLast = false;
	// Feature flags / UI toggles
	showResetControls = false; // Hidden: reset will be triggered via payload later

	// Purchase history (from warehouse-scaninout)
	history: any = false;
	historyloading = false;

	// Enhanced scan capture
	public keyBuffer = '';
	private readonly MIN_BARCODE_LENGTH = 4; // Minimum barcode length
	private readonly MAX_BARCODE_LENGTH = 50; // Maximum barcode length

	// Memory leak prevention - subscription tracking
	private subscriptions: Subscription[] = [];
	private timeouts: any[] = [];
	private intervals: any[] = [];
	private resizeListener: (() => void) | null = null;

	// Socket connection status
	socketConnected = false;
	socketStatusMessage = 'Connecting...';

	constructor(
		private formBuilder: UntypedFormBuilder,
		private locationService: Location,
		private inventoryService: InventoryService,
		private globalSearchService: GlobalSearchService,
		private purchasingService: PurchasingService,
		private omsService: OmsService,
		private vendorService: VendorService,
		private ordersService: OrdersService,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();

		// Initialize form
		this.scanForm = this.formBuilder.group({
			stockid: [''],
			quantity: [0, Validators.required]
		});

		// Get user info
		const userSubscription = this.globalSearchService.user.subscribe((u: any) => {
			this.user = u;
			this.location = u.user.defaultlocation.loccode;
		});
		this.subscriptions.push(userSubscription);

		// Setup OMS connection
		this.globalSearchService.mesagesallowed.next(false);
		this.token = this.globalSearchService.randomString(12, '0123456789abcdefghijklmnopqrstuvwxyz');
		this.omsService.setupSocketConnection(this.token);
	}

	ngOnInit(): void {
		// Detect mobile device
		this.detectMobile();

		// Load vendors
		this.loadVendors();

		// Subscribe to send/receive toggle changes.
		// Scan-out forces bulk mode so the user must type a quantity before the scan
		// applies — single-tire auto-receive is unsafe for outbound RGA (one scan
		// per tire would mis-count multi-tire returns).
		const sendToggleSubscription = this.sendToggle.valueChanges.subscribe(value => {
			if (value === true) {
				this.modeBeforeSendOut = this.scanMode;
				this.searchModeBeforeSendOut = this.isSearchMode;
				if (this.scanMode !== 'bulk') {
					this.scanMode = 'bulk';
					this.isSearchMode = true;
					this.disableKeyboardListener();
				}
			} else {
				this.scanMode = this.modeBeforeSendOut;
				this.isSearchMode = this.searchModeBeforeSendOut;
				if (this.isSearchMode) {
					this.disableKeyboardListener();
				} else {
					this.enableKeyboardListener();
				}
				// clear any half-entered bulk qty when leaving scan-out
				this.bulkQuantity = 0;
				this.bulkQuantityInput = 0;
			}
			if (this.currentItem && this.currentItem.stockid) {
				this.loadData(this.currentItem.stockid);
			}
		});
		this.subscriptions.push(sendToggleSubscription);

		// Subscribe to scan updates
		this.omsService.subscribeToRecScanUpdate((err, data) => {
			// Filter inbound updates by vendor, location, and ignore our own echo via sourceToken
			const msg: any = data?.data;
			const msgItem: any = msg?.message?.item;
			const msgVendor: any = msgItem?.vendor;
			const msgLoc: any = msgItem?.loccode;
			const sourceToken: any = msg?.sourceToken;

			// Drop our own echoed events to avoid double-playing and duplicate refreshes
			if (sourceToken === this.token) {
				return;
			}

			// Only react if vendor and location match the current device context
			if (!msgVendor || !msgLoc || msgVendor !== this.vendor_selected || msgLoc !== this.location) {
				return;
			}

			// Extract the scanned item information for audio playback
			const scannedStockid = msgItem?.stockid;
			const action = msgItem?.action;
			console.log(data);

			// Only play audio for actual scan events (not removals, updates, etc.)
			if (scannedStockid && action !== 'removed' && action !== 'updated' && action !== 'deleted') {
				// Get destination and play audio for the scanned item
				this.playAudioForScannedItem(scannedStockid);
			}

			// Always refresh scan history but don't reload current item data to prevent double audio
			this.hadScansBeforeRefresh = this.scan_history && this.scan_history.length > 0;
			const recScanSubscription = this.purchasingService.getRecs('').subscribe((recscanitems: any) => {
				const hadScans = this.hadScansBeforeRefresh;
				const hasScansNow = recscanitems && recscanitems.length > 0;

				// Detect finalize rec: had scans before, now empty
				if (hadScans && !hasScansNow) {
					this.clearStateAfterFinalize();
				}

				this.scan_history = recscanitems;
				this.hadScansBeforeRefresh = hasScansNow;
				// Don't call loadData() here to prevent double audio - let user manually refresh if needed
			});
			this.subscriptions.push(recScanSubscription);
		});

		// Load initial scan history
		const initialScanHistorySubscription = this.purchasingService.getRecs('').subscribe((recscanitems: any) => {
			this.scan_history = recscanitems;
			this.hadScansBeforeRefresh = recscanitems && recscanitems.length > 0;
		});
		this.subscriptions.push(initialScanHistorySubscription);

		// Start socket status monitoring
		this.startSocketStatusMonitoring();
	}

	ngOnDestroy(): void {
		// Cleanup time update interval
		this.stopTimeUpdateInterval();

		// Cleanup all subscriptions
		this.subscriptions.forEach(subscription => {
			if (subscription && !subscription.closed) {
				subscription.unsubscribe();
			}
		});
		this.subscriptions = [];

		// Cleanup all timeouts
		this.timeouts.forEach(timeoutId => {
			clearTimeout(timeoutId);
		});
		this.timeouts = [];

		// Cleanup all intervals
		this.intervals.forEach(intervalId => {
			clearInterval(intervalId);
		});
		this.intervals = [];

		// Cleanup window resize event listener
		if (this.resizeListener) {
			window.removeEventListener('resize', this.resizeListener);
			this.resizeListener = null;
		}
	}

	ngAfterViewInit(): void {
		// Force readonly on hidden input to prevent keyboard
		if (this.hiddenScanInput) {
			this.hiddenScanInput.nativeElement.setAttribute('readonly', 'readonly');
			this.hiddenScanInput.nativeElement.setAttribute('inputmode', 'none');
		}

		// Focus scanner input after view init to prepare for scanning
		setTimeout(() => {
			this.focusScannerInput();
		}, 100);
	}

	// KEYBOARD PREVENTION METHODS

	@HostListener('document:keydown', ['$event'])
	onDocumentKeyDown(event: KeyboardEvent): void {
		const target = event.target as HTMLElement;


		// Allow normal input in search mode for search input fields
		if (this.isSearchMode && (
			target.classList.contains('search-input-field') ||
				target.classList.contains('mat-input-element') ||
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA'
		)) {
			return; // Don't prevent default behavior for search inputs
		}

		// Allow normal input when barcode add form is displayed
		if (this.showBarcodeAddForm) {
			return; // Don't prevent default behavior - allow all keyboard input
		}

		// Skip keyboard listener if disabled
		if (!this.keyboardListenerEnabled) {
			return;
		}

		// Handle barcode scanner input FIRST - build buffer but don't process until Enter
		// Only build buffer in scanner mode, not search mode
		if (event.key.length === 1 && this.isValidScanCharacter(event.key) && !this.isSearchMode) {
			this.keyBuffer += event.key;
			this.isScanning = true;
			// Prevent default behavior to avoid keyboard popup
			event.preventDefault();
			return; // Don't process yet, just build buffer
		}

		// Handle Enter key - process complete barcode (barcode scanner completion)
		// Only process in scanner mode, not search mode
		if (event.key === 'Enter' && !this.isSearchMode) {
			event.preventDefault();
			if (this.keyBuffer.trim()) {
				this.processScanInput(this.keyBuffer.trim());
				this.clearScanBuffer();
			}
		}

		// Handle Escape key - clear buffer
		if (event.key === 'Escape') {
			this.clearScanBuffer();
		}
	}

	onHiddenInputFocus(): void {
		// Immediately blur to prevent keyboard popup
		if (this.hiddenScanInput) {
			this.hiddenScanInput.nativeElement.blur();
		}
	}

	onHiddenInputKeyDown(event: KeyboardEvent): void {
		// Only handle Enter key, let other keys pass through to document listener
		if (event.key === 'Enter') {
			event.preventDefault();
			// Use the keyBuffer instead of the form control value to avoid conflicts
			if (this.keyBuffer.trim()) {
				this.processScanInput(this.keyBuffer.trim());
				this.clearScanBuffer();
			}
		}
	}

	// Override any focus events that might trigger keyboard
	@HostListener('focusin', ['$event'])
	onFocusIn(event: FocusEvent): void {
		const target = event.target as HTMLElement;
		// Only allow focus on specific safe elements
		if (!this.isSafeFocusTarget(target)) {
			event.preventDefault();
			event.stopPropagation();
			(event.target as HTMLElement).blur();
		}
	}

	private isSafeFocusTarget(element: HTMLElement): boolean {
		// Allow focus only on buttons, mat-button-toggle, and input fields in appropriate modes
		return element.tagName === 'BUTTON' ||
			element.classList.contains('mat-button-toggle-button') ||
			// Allow scanner input to be focusable for mobile tap functionality
			element.classList.contains('scanner-input') ||
			(this.isSearchMode && (
				element.classList.contains('search-input-field') ||
				element.classList.contains('search-button') ||
				element.classList.contains('mat-input-element') ||
				element.tagName === 'INPUT' ||
				element.tagName === 'TEXTAREA'
			));
	}

	onQuantityInputFocus(): void {
		// For quantity input, we actually want the keyboard to show
		// since users need to type numbers, but we can add any special handling here
	}

	/**
	 * Solution 3: Camera-based barcode scanner
	 * Alternative input method that doesn't require keyboard
	 */
	onBarcodeScanned(barcode: string): void {
		this.processScanInput(barcode);
	}

	onScannerStarted(started: boolean): void {
		// Handle scanner started event if needed
	}

	// ENHANCED SCAN CAPTURE METHODS

	private isValidScanCharacter(key: string): boolean {
		// Allow alphanumeric characters, common barcode symbols, and special characters
		return /^[a-zA-Z0-9\-_\.\+\*\#\@\$\%\&\=\!\?\(\)\[\]\{\}\|\\\/\:\;\<\>\,\~\`\s]$/.test(key);
	}


	private clearScanBuffer(): void {
		this.keyBuffer = '';
		this.isScanning = false;
	}


	private showScanError(message: string): void {
		this.scanStatus = 'error';
		this.statusMessage = message;
		this.playWarningSound();

		// Clear error after 3 seconds
		const timeoutId = setTimeout(() => {
			if (this.scanStatus === 'error') {
				this.scanStatus = 'idle';
				this.statusMessage = '';
			}
		}, 3000);
		this.timeouts.push(timeoutId);
	}

	private showVendorRequiredError(): void {
		this.scanStatus = 'error';
		this.statusMessage = '⚠️ VENDOR REQUIRED - Please select a vendor first!';
		this.playBuzzerSound();
		this.vibrateDevice();

		// Clear error after 4 seconds (longer for important message)
		const timeoutId = setTimeout(() => {
			if (this.scanStatus === 'error') {
				this.scanStatus = 'idle';
				this.statusMessage = '';
			}
		}, 4000);
		this.timeouts.push(timeoutId);
	}

	private showScanWarning(message: string): void {
		this.scanStatus = 'warning';
		this.statusMessage = message;

		// Clear warning after 2 seconds
		const timeoutId = setTimeout(() => {
			if (this.scanStatus === 'warning') {
				this.scanStatus = 'idle';
				this.statusMessage = '';
			}
		}, 2000);
		this.timeouts.push(timeoutId);
	}

	// CORE SCANNING LOGIC

	private processScanInput(barcode: string): void {
		if (!barcode || barcode.trim() === '') {return;}

		// Check if vendor is selected before processing scan
		if (!this.selectedVendor || this.vendor_selected === 'DEFAULT') {
			this.showVendorRequiredError();
			this.clearScanBuffer();
			return;
		}

		// Basic validation - only check for reasonable length and non-empty
		const trimmed = barcode.trim();
		if (trimmed.length < 1 || trimmed.length > 100) {
			this.showScanError('Invalid barcode format. Please try again.');
			this.clearScanBuffer();
			return;
		}

		// Update last scan time
		this.lastScanTime = Date.now();
		this.startTimeUpdateInterval();

		// Clear any existing item before processing new scan
		this.clearCurrentItem();

		this.isScanning = true;
		this.scanStatus = 'processing';
		this.statusMessage = 'Processing scan...';

		// Use the same method as warehouse-item-scan-two
		this.lookupVersionEight(barcode.trim());
	}

	private validateBarcode(barcode: string): boolean {
		// Basic barcode validation
		const trimmed = barcode.trim();

		// Check length
		if (trimmed.length < this.MIN_BARCODE_LENGTH || trimmed.length > this.MAX_BARCODE_LENGTH) {
			return false;
		}

		// Check for valid characters (alphanumeric and common barcode symbols)
		if (!/^[a-zA-Z0-9\-_\.\+\*\#\@\$\%\&\=\!\?\(\)\[\]\{\}\|\\\/\:\;\<\>\,\~\`]+$/.test(trimmed)) {
			return false;
		}

		// Check for common barcode patterns
		const commonPatterns = [
			/^[A-Z]{2,4}-\d{6,12}$/, // SKU pattern like ADV-1951340505
			/^\d{8,14}$/, // Numeric barcodes
			/^[A-Z0-9]{8,20}$/, // Alphanumeric codes
		];

		return commonPatterns.some(pattern => pattern.test(trimmed));
	}

	private lookupVersionEight(barcode: string): void {
		// Use the same search method as warehouse-item-scan-two
		const searchData = { stockid: barcode };

		const itemSearchSubscription = this.inventoryService.itemSearch(searchData).subscribe({
			next: (results: any) => {
				this.handleScanResults(results, barcode);
			},
			error: (error) => {
				this.handleScanError(error, barcode);
			}
		});
		this.subscriptions.push(itemSearchSubscription);
	}

	private handleScanResults(results: any, barcode: string): void {
		this.isScanning = false;

		if (results && results.length > 0) {
			const item = results[0];
			this.currentItem = item;

			// Load data for this item (same as warehouse-scaninout)
			// This will call getItemDestination() which plays the proper destination audio (stock.mp3, staging.mp3, sticker.mp3)
			this.loadData(item.stockid);

			// AUTO-RECEIVE in Every Tire Mode (scan-in only — scan-out forces bulk).
			// Scan-out + bulk + qty already typed → apply that qty against the just-scanned item.
			if (this.scanMode === 'single' && !this.sendToggle.value) {
				this.autoReceiveOne();
			} else if (this.scanMode === 'bulk' && this.sendToggle.value && this.bulkQuantity > 0) {
				this.recordBulkScan();
			} else {
				// Display and wait for manager action (Receive button)
				this.scanStatus = 'success';
				this.statusMessage = `Found: ${item.description}`;
				this.binLocation = item.bin || 'Unknown';
			}

			// Update form
			this.scanForm.get('stockid')?.setValue(item.stockid);

			// Scroll to result after successful scan
			this.scrollToScanResult();

		} else {
			// Item not found - show modal with add barcode option
			this.searchResults = []; // Clear search results so modal content shows
			this.scanStatus = 'warning';
			this.statusMessage = '🔍 ITEM NOT FOUND - Barcode not in system';
			this.lastSearchTerm = barcode;
			this.showBarcodeAddModal = true;
			this.showBarcodeAddForm = false;

			// Store original mode (scan mode)
			this.originalMode = 'scan';

			// Disable keyboard capture during barcode add process
			this.disableKeyboardListener();

			// Play different buzzer sound for item not found
			this.playItemNotFoundBuzzer();
			this.vibrate();
		}
	}

	private handleScanError(error: any, barcode: string): void {
		this.isScanning = false;
		this.scanStatus = 'error';
		this.statusMessage = 'Scan error occurred';
		this.audioPlayBad();
		this.vibrate();
	}

	// WAREHOUSE DATA LOADING (from warehouse-scaninout)

	private loadData(stockid: string, playAudio = true): void {
		if (!this.currentItem) {
			return;
		}

		// Get destination information for this item
		this.getItemDestination(stockid, playAudio);

		// Extract PO data from the item response first
		this.extractPODataFromItem();

		// Only load additional PO data if we don't have any from the item response
		if (this.sendToggle.value) {
			this.loadRgaData(stockid);
		} else {
			// Only call loadPurchaseOrders if we don't have PO data from the item
			// Check if we have valid PO data (either pending items or ordered items)
			if (!this.purchaseOrders || (this.purchaseOrders.total_pending === 0 && this.purchaseOrders.original_inbound === 0)) {
				this.loadPurchaseOrders(stockid);
			}
		}

		// Calculate already scanned quantity using the new method
		this.already_scanned = this.getTotalScanned();

		// Calculate expected quantity
		if (this.currentItem && this.currentItem.stockid === this.MST_ITEM_TYPE) {
			this.receiving_quantity = this.expected - this.already_scanned;
		} else if (this.currentItem) {
			this.receiving_quantity = this.currentItem.openpos - this.already_scanned;
		}

		let recvalue = this.receiving_quantity;
		if (this.receiving_quantity < 0) {
			recvalue = 0;
		}

		// Set the quantity in the form
		this.qtyRec.setValue(recvalue);

		// Don't auto-set bulk quantity input - let manager enter their own quantity
		// this.bulkQuantityInput = Math.max(0, recvalue);

		// Prepare item details for warehouse-scaninout component
		this.prepareItemDetailsForScaninout();

		// Load purchase history
		this.loadPurchaseHistory(stockid);
	}

	// BULK MODE METHODS

	setBulkQuantity(quantity: number): void {
		this.bulkQuantity = quantity;
	}

	setBulkQuantityInput(quantity: number): void {
		this.bulkQuantityInput = quantity;
	}

	setBulkQuantityFromInput(): void {
		if (this.bulkQuantityInput && this.bulkQuantityInput > 0) {
			this.bulkQuantity = this.bulkQuantityInput;
		}
	}


	// AUDIO AND VIBRATION (same as other warehouse components)

	// Audio context management
	private audioContext: AudioContext | null = null;

	private getAudioContext(): AudioContext {
		if (!this.audioContext) {
			this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
		}
		return this.audioContext;
	}

	private audioPlay(): void {
		// Play success sound using Web Audio API
		try {
			const audioContext = this.getAudioContext();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);

			// Success sound: pleasant beep
			oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
			oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);

			oscillator.type = 'sine';
			gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.3);
		} catch (error) {
			console.warn('Success sound failed:', error);
		}
	}

	private audioPlayBad(): void {
		// Play error sound using Web Audio API
		try {
			const audioContext = this.getAudioContext();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);

			// Error sound: harsh buzz
			oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
			oscillator.frequency.setValueAtTime(150, audioContext.currentTime + 0.1);
			oscillator.frequency.setValueAtTime(200, audioContext.currentTime + 0.2);

			oscillator.type = 'square';
			gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.5);
		} catch (error) {
			console.warn('Error sound failed:', error);
		}
	}

	playSuccessSound(): void {
		//this.audioPlay();
	}

	playWarningSound(): void {
		this.audioPlayBad();
	}

	playBuzzerSound(): void {
		// Play a more aggressive buzzer sound for vendor requirement
		try {
			// Create a buzzer-like sound using Web Audio API
			const audioContext = this.getAudioContext();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);

			// Buzzer sound: low frequency, harsh tone
			oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
			oscillator.frequency.setValueAtTime(150, audioContext.currentTime + 0.1);
			oscillator.frequency.setValueAtTime(200, audioContext.currentTime + 0.2);

			oscillator.type = 'square'; // Harsh square wave
			gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.5);
		} catch (error) {
			// Fallback to existing warning sound if Web Audio API fails
			console.warn('Buzzer sound failed, using fallback:', error);
			this.audioPlayBad();
		}
	}

	playItemNotFoundBuzzer(): void {
		// Play a different buzzer sound for item not found
		try {
			// Create a different buzzer pattern using Web Audio API
			const audioContext = this.getAudioContext();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);

			// Different buzzer pattern: higher frequency, more "beep beep beep" pattern
			oscillator.frequency.setValueAtTime(400, audioContext.currentTime);     // First beep
			oscillator.frequency.setValueAtTime(0, audioContext.currentTime + 0.1); // Silence
			oscillator.frequency.setValueAtTime(400, audioContext.currentTime + 0.2); // Second beep
			oscillator.frequency.setValueAtTime(0, audioContext.currentTime + 0.3); // Silence
			oscillator.frequency.setValueAtTime(400, audioContext.currentTime + 0.4); // Third beep

			oscillator.type = 'sine'; // Softer sine wave for different tone
			gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
			gainNode.gain.setValueAtTime(0, audioContext.currentTime + 0.1);
			gainNode.gain.setValueAtTime(0.2, audioContext.currentTime + 0.2);
			gainNode.gain.setValueAtTime(0, audioContext.currentTime + 0.3);
			gainNode.gain.setValueAtTime(0.2, audioContext.currentTime + 0.4);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.7);

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.7);
		} catch (error) {
			// Fallback to existing warning sound if Web Audio API fails
			console.warn('Item not found buzzer failed, using fallback:', error);
			this.audioPlayBad();
		}
	}

	playDestinationAudio(destination: string): void {
		// Play audio based on destination type
		let audioFile = '';

		switch (destination) {
		case 'stock':
			 audioFile = '/assets/stock.mp3';
			break;
		case 'staging':
			 audioFile = '/assets/staging.mp3';
			break;
		case 'sticker':
			audioFile = '/assets/sticker.mp3';
			break;
		case 'notonpo':
			// Use error sound for items not on PO
			 audioFile = '/assets/overrec.mp3';
			 break;
		case 'nopo':
			audioFile = '/assets/nopo.mp3';
			break;
		default:
			// Default to stock sound
			audioFile = '/assets/stock.mp3';
			break;
		}


		if (audioFile) {
			const audio = new Audio(audioFile);

			audio.volume = 0.9;
			// Add event listeners for debugging
			audio.addEventListener('error', (e) => console.error('Audio error:', audioFile, e));

			audio.play().catch(e => {
				// Fallback to existing audio if destination audio fails
				this.audioPlay();
			});
		}
	}

	private playAudioForScannedItem(stockid: string): void {
		// Get destination information for the scanned item
		const data = {
			stockid: stockid,
			loccode: this.location,
			vendor: this.vendor_selected
		};

		const itemDestinationSubscription = this.inventoryService.getItemDestination(data).subscribe({
			next: (result: any) => {
				const scannedQty = parseInt(result.scannedQty) || 0;
				const expectedQty = parseInt(result.expectedQty) || parseInt(result.stats?.openpos) || 0;

				// Determine audio destination based on business rules
				const audioDestination = this.determineAudioDestination(result, scannedQty, expectedQty);

				this.playDestinationAudio(audioDestination);
			},
			error: (error) => {
				// Default to stock sound if destination lookup fails
				this.playDestinationAudio('stock');
			}
		});
		this.subscriptions.push(itemDestinationSubscription);
	}

	private determineAudioDestination(result: any, scannedQty: number, expectedQty: number): string {
		return result.hasNoPO ? 'nopo' :
					 result.isOverReceived || (expectedQty === 0 && scannedQty > 0) || scannedQty > expectedQty ? 'notonpo' :
					 result.destination === 'staging' ? 'staging' :
					 result.destination === 'sticker' ? 'sticker' :
					 'stock';
	}


	vibrateDevice(): void {
		this.vibrate();
	}

	private vibrate(): void {
		if (window.navigator && window.navigator.vibrate) {
			navigator.vibrate(2000);
		} else {
			// Not supported
		}
	}


	// NAVIGATION

	goBack(): void {
		this.locationService.back();
	}

	clearCurrentItem(): void {
		this.currentItem = null;
		this.itemDetailsForScaninout = null; // Clear item details
		this.scanStatus = 'idle';
		this.statusMessage = '';
		this.binLocation = '';
		this.bulkQuantity = 0;
		this.bulkQuantityInput = 0;
		this.showDetails = false; // Hide details when clearing item
		this.scanForm.reset();
	}

	// CORE RECEIVING FUNCTIONALITY

	private autoReceiveOne(): void {
		if (!this.currentItem || !this.currentItem.stockid) {
			console.warn('autoReceiveOne: currentItem or stockid is not set.');
			return;
		}
		if (this.sendToggle.value) {
			// Scan-out never auto-receives one — the operator must type a bulk qty first.
			return;
		}

		this.receiving_quantity = 1;

		const item: any = {
			quantityrecd: this.receiving_quantity,
			stockid: this.currentItem.stockid,
			loccode: this.location,
			bin: this.qtyBin.value,
			vendor: this.vendor_selected,
			sending: this.sendToggle.value ? 1 : 0
		};

		if (item.stockid === this.MST_ITEM_TYPE) {
			item['orderno'] = this.mstdetails?.orderno;
		} else {
		}

		const data = { item, user: this.user.user };

		const addRecSubscription = this.inventoryService.addRecItem(data).subscribe({
			next: (response) => {

				// Show quick success feedback
				this.scanStatus = 'success';
				this.statusMessage = `Added 1 ${this.currentItem?.stockid || 'item'} to ${this.qtyBin.value}`;
				this.vibrateDevice();

				// Send OMS update
				this.sendUpdateRec(data);

				// Automatically create/update RGA in scan-out mode
				if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
					// Calculate total scanned quantity (current total + this quantity)
					// Note: scan_history may not be updated yet, so we add receiving_quantity to current total
					const currentTotal = this.getTotalScannedForItem(this.currentItem.stockid);
					const totalScanned = currentTotal + this.receiving_quantity;
					this.createOrUpdateRgaForItem(this.currentItem, totalScanned).catch(error => {
						// Don't block scan success if RGA creation fails
						console.error('Error creating/updating RGA:', error);
					});
				}

				// Clear status after 2 seconds but keep item visible
				const timeoutId = setTimeout(() => {
					this.scanStatus = 'idle';
					this.statusMessage = '';
					// Keep item visible for user to see details or scan next item
				}, 2000);
				this.timeouts.push(timeoutId);

				// Refresh history
				this.refreshScanHistory();
			},
			error: (error) => {
				this.scanStatus = 'error';
				this.statusMessage = 'Failed to add item';
			}
		});
		this.subscriptions.push(addRecSubscription);
	}

	async recordScan (): Promise < void > {
		if (!this.currentItem || !this.currentItem.stockid || this.receiving_quantity <= 0) {
			this.scanStatus = 'error';
			this.statusMessage = 'Invalid scan data';
			this.audioPlayBad();
			this.vibrate();
			return;
		}

		const item: any = {
			quantityrecd: this.receiving_quantity,
			stockid: this.currentItem.stockid,
			loccode: this.location,
			bin: this.receiving_bin,
			vendor: this.vendor_selected,
			sending: this.sendToggle.value ? 1 : 0 // Respect scan in/out toggle
		};

		if (item.stockid === this.MST_ITEM_TYPE) {
			item['orderno'] = this.mstdetails?.orderno;
		}

		// Use selected PO if PO selection is enabled
		if (this.selectedPoDetails && this.enablePoSelection && !this.sendToggle.value) {
			item['ponumber'] = this.selectedPoDetails.orderno;
		}

		const data = { item, user: this.user.user };

		try {
			this.qtyAdded = true;
			await this.inventoryService.addRecItem(data).toPromise();

			this.sendUpdateRec(data);

			// Automatically create/update RGA in scan-out mode
			if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
				// Calculate total scanned quantity (current total + this quantity)
				// Note: scan_history may not be updated yet, so we add receiving_quantity to current total
				const currentTotal = this.getTotalScannedForItem(this.currentItem.stockid);
				const totalScanned = currentTotal + this.receiving_quantity;
				this.createOrUpdateRgaForItem(this.currentItem, totalScanned).catch(error => {
					// Don't block scan success if RGA creation fails
					console.error('Error creating/updating RGA:', error);
				});
			}

			this.scanStatus = 'success';
			this.statusMessage = `${this.receiving_quantity} items recorded`;
			this.audioPlay();

			// Clear current item and reset
			this.receiving_quantity = 0;
			this.qtyAdded = false;
			this.clearCurrentItem();
			this.refreshScanHistory();

		} catch (error) {
			this.scanStatus = 'error';
			this.statusMessage = 'Failed to record scan';
			this.audioPlayBad();
			this.vibrate();
			this.qtyAdded = false;
		}
	}

	recordBulkScan(): void {
		if (this.bulkQuantity > 0 && this.currentItem && this.currentItem.stockid) {
			this.receiving_quantity = this.bulkQuantity;
			this.recordScan();
			this.bulkQuantity = 0;
			this.bulkQuantityInput = 0;
		}
	}

	receiveBulkQuantity(): void {
		if (!this.bulkQuantityInput || this.bulkQuantityInput <= 0 || !this.currentItem || !this.currentItem.stockid) {
			this.scanStatus = 'error';
			this.statusMessage = 'Please enter a valid quantity';
			return;
		}

		this.qtyAdded = true;

		// Use the exact quantity from the input field
		const quantityToReceive = this.bulkQuantityInput;

		const item: any = {
			quantityrecd: quantityToReceive,
			stockid: this.currentItem.stockid,
			loccode: this.location,
			bin: this.qtyBin.value,
			vendor: this.vendor_selected,
			sending: this.sendToggle.value ? 1 : 0
		};

		if (item.stockid === this.MST_ITEM_TYPE) {
			item['orderno'] = this.mstdetails?.orderno;
		}

		// Use selected PO if PO selection is enabled
		if (this.selectedPoDetails && this.enablePoSelection && !this.sendToggle.value) {
			item['ponumber'] = this.selectedPoDetails.orderno;
		}

		const data = { item, user: this.user.user };

		const receiveBulkSubscription = this.inventoryService.addRecItem(data).subscribe({
			next: (response) => {
				this.scanStatus = 'success';
				this.statusMessage = `Received ${quantityToReceive} ${this.currentItem?.stockid || 'items'}`;
				this.playSuccessSound();
				this.vibrateDevice();

				// Send OMS update
				this.sendUpdateRec(data);

				// Automatically create/update RGA in scan-out mode
				if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
					// Calculate total scanned quantity (current total + this bulk quantity)
					const currentTotal = this.getTotalScannedForItem(this.currentItem.stockid);
					const totalScanned = currentTotal + quantityToReceive;
					this.createOrUpdateRgaForItem(this.currentItem, totalScanned).catch(error => {
						console.error('Error creating/updating RGA:', error);
					});
				}

				// Clear bulk input after success but keep item visible
				const timeoutId = setTimeout(() => {
					this.bulkQuantityInput = 0;
					this.qtyAdded = false;
					this.scanStatus = 'idle';
					this.statusMessage = '';
					// Keep item visible for user to see details or scan next item
				}, 2000);
				this.timeouts.push(timeoutId);

				// Refresh history
				this.refreshScanHistory();
			},
			error: (error) => {
				this.scanStatus = 'error';
				this.statusMessage = 'Failed to receive bulk quantity';
				this.qtyAdded = false;
			}
		});
		this.subscriptions.push(receiveBulkSubscription);
	}

	sendUpdateRec(message: any): void {
		const data = {
			message: message,
			sourceToken: this.token // identify origin device to avoid echo handling
		};
		this.omsService.sendRecScanUpdate({ data, roomName: this.CHAT_ROOM }, () => {});
	}

	updatePutAny(): void {
		if (this.receiving_quantity && this.currentItem && this.currentItem.stockid) {
			const item = {
				quantityrecd: this.receiving_quantity,
				stockid: this.currentItem.stockid,
				loccode: this.location,
				bin: this.qtyBin.value,
				vendor: this.vendor_selected,
				sending: this.sendToggle.value ? 1 : 0
			};

			if (item.stockid === 'MST') {
				item['orderno'] = this.mstdetails?.orderno;
			}

			const data = { item, user: this.user.user };

			if (data) {
				const updatePutAnySubscription = this.inventoryService.addRecItem(data).subscribe(r => {
					this.sendUpdateRec(data);
					this.globalSearchService.showNotification(this.receiving_quantity + ' Added', 'success', 'top', 'left');

					// Automatically create/update RGA in scan-out mode
					if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
						// Calculate total scanned quantity (current total + this quantity)
						const currentTotal = this.getTotalScannedForItem(this.currentItem.stockid);
						const totalScanned = currentTotal + this.receiving_quantity;
						this.createOrUpdateRgaForItem(this.currentItem, totalScanned).catch(error => {
							console.error('Error creating/updating RGA:', error);
						});
					}

					this.receiving_quantity = 0;
					const getRecsSubscription = this.purchasingService.getRecs('').subscribe((recscanitems: any) => {
						this.scan_history = recscanitems;
						if (this.currentItem && this.currentItem.stockid) {
							this.loadData(this.currentItem.stockid);
						}
					});
					this.subscriptions.push(getRecsSubscription);
				});
				this.subscriptions.push(updatePutAnySubscription);
			}
		}
	}

	async autoRec (): Promise < void > {
		if (!this.currentItem || !this.currentItem.stockid) {
			console.warn('autoRec: currentItem or stockid is not set.');
			return;
		}
		if (this.receiving_quantity) {
			const item: any = {
				quantityrecd: this.receiving_quantity,
				stockid: this.currentItem.stockid,
				loccode: this.location,
				bin: this.qtyBin.value,
				vendor: this.vendor_selected,
				sending: this.sendToggle.value ? 1 : 0
			};

			if (item.stockid === 'MST') {
				item['orderno'] = this.mstdetails?.orderno;
			}

			const data = { item, user: this.user.user };
			this.qtyAdded = true;
			if (data) {
				try {
					await this.inventoryService.addRecItem(data).toPromise();

					this.sendUpdateRec(data);

					// Automatically create/update RGA in scan-out mode
					if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
						// Calculate total scanned quantity (current total + this quantity)
						const currentTotal = this.getTotalScannedForItem(this.currentItem.stockid);
						const totalScanned = currentTotal + this.receiving_quantity;
						this.createOrUpdateRgaForItem(this.currentItem, totalScanned).catch(error => {
							console.error('Error creating/updating RGA:', error);
						});
					}

					this.receiving_quantity = 0;
					this.qtyAdded = false;
					const recscanitems: any = await this.purchasingService.getRecs('').toPromise();
					this.scan_history = recscanitems;
					if (this.currentItem && this.currentItem.stockid) {
						this.loadData(this.currentItem.stockid);
					}

				} catch (error) {
					this.globalSearchService.showNotification(
						'Error adding item',
						'danger',
						'top',
						'left'
					);
				}
			}
		}
	}

	private refreshScanHistory(): void {
		const refreshSubscription = this.purchasingService.getRecs('').subscribe((recscanitems: any) => {
			const hadScans = this.hadScansBeforeRefresh;
			const hasScansNow = recscanitems && recscanitems.length > 0;

			// Detect finalize rec: had scans before, now empty
			if (hadScans && !hasScansNow) {
				this.clearStateAfterFinalize();
			}

			this.scan_history = recscanitems;
			this.hadScansBeforeRefresh = hasScansNow;

			// Force change detection to ensure UI updates
			this.cdr.detectChanges();
		});
		this.subscriptions.push(refreshSubscription);
	}

	private clearStateAfterFinalize(): void {
		// Clear all item-related state
		this.clearCurrentItem();

		// Clear purchase orders and RGA data
		this.purchaseOrders = null;
		this.vendorRgas = null;
		this.expectedQuantity = 0;

		// Clear history
		this.history = null;

		// Reset receiving quantities
		this.receiving_quantity = 0;
		this.bulkQuantity = 0;
		this.bulkQuantityInput = 0;

		// Clear MST details if any
		this.mstdetails = null;

		// Clear selected PO details
		this.selectedPoDetails = null;

		// Reset status
		this.scanStatus = 'idle';
		this.statusMessage = '';

		// Hide details view
		this.showDetails = false;

		// Show success notification
		this.globalSearchService.showNotification(
			'Receiving session finalized successfully!',
			'success',
			'top',
			'left'
		);
	}

	// SCAN HISTORY MANAGEMENT

	toggleScanHistory(): void {
		this.showScanHistory = !this.showScanHistory;
	}

	toggleAdvancedDrawer(): void {
		this.showAdvancedDrawer = !this.showAdvancedDrawer;
	}

	removeScan(scan: any): void {
		if (confirm(`Undo scan of ${scan.stockid}?`)) {
			// Send negative quantity to reverse the scan
			const item: any = {
				quantityrecd: -1, // Always undo just 1 item
				stockid: scan.stockid,
				loccode: this.location,
				bin: scan.bin || this.qtyBin.value,
				vendor: scan.vendor || this.vendor_selected,
				sending: this.sendToggle.value ? 1 : 0
			};

			if (item.stockid === 'MST') {
				item['orderno'] = this.mstdetails?.orderno;
			}

			const data = { item, user: this.user.user };

			const removeScanSubscription = this.inventoryService.addRecItem(data).subscribe({
				next: (response) => {
					this.scanStatus = 'warning';
					this.statusMessage = `Undid ${scan.qty} ${scan.stockid}`;
					this.playWarningSound();

					// Send OMS update for the reversal
					this.sendUpdateRec(data);

					// Update RGA if in scan-out mode - decrease quantity by the amount undone
					if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
						const quantityToDecrease = scan.qty || 1;
						let rgaOrderno: string | null = null;

						// Check itemRgaMap first (for backward compatibility)
						if (this.itemRgaMap[scan.stockid]) {
							rgaOrderno = this.itemRgaMap[scan.stockid];
						} else if (this.vendorRgaMap[scan.vendor || this.vendor_selected]) {
							// Check vendorRgaMap as fallback
							rgaOrderno = this.vendorRgaMap[scan.vendor || this.vendor_selected];
						}

						if (rgaOrderno) {
							// RGA exists in map - decrease quantity
							const updateData = {
								orderno: rgaOrderno,
								stockid: scan.stockid,
								quantity: -quantityToDecrease,
								action: 'add',
								description: scan.description || ''
							};
							this.purchasingService.updateRgaItem(updateData).subscribe({
								next: () => {
									// Update maps after successful update
									this.itemRgaMap[scan.stockid] = rgaOrderno;
									this.vendorRgaMap[scan.vendor || this.vendor_selected] = rgaOrderno;
								},
								error: (error) => console.error('Error updating RGA:', error)
							});
						} else {
							// Not in map - check for existing RGA by vendor (not item)
							const vendorSearch = {
								vendor: scan.vendor || this.vendor_selected
								// Note: No stockid - this finds ANY open RGA for the vendor
							};
							this.purchasingService.getRgasForItemByVendor(vendorSearch).subscribe({
								next: (existingRgas: any) => {
									if (existingRgas && existingRgas.details && existingRgas.details.length > 0) {
										const rgaLine = existingRgas.details[0];
										rgaOrderno = rgaLine.orderno;
										// Update both maps
										this.itemRgaMap[scan.stockid] = rgaOrderno;
										this.vendorRgaMap[scan.vendor || this.vendor_selected] = rgaOrderno;

										const updateData = {
											orderno: rgaOrderno,
											stockid: scan.stockid,
											quantity: -quantityToDecrease,
											action: 'add',
											description: scan.description || ''
										};
										this.purchasingService.updateRgaItem(updateData).subscribe({
											next: () => {},
											error: (error) => console.error('Error updating RGA:', error)
										});
									}
								},
								error: (error) => console.error('Error checking for RGA:', error)
							});
						}
					}

					// Refresh scan history
					this.refreshScanHistory();
					this.globalSearchService.showNotification('Scan undone', 'success', 'top', 'left');
				},
				error: (error) => {
					this.globalSearchService.showNotification('Failed to undo scan', 'danger', 'top', 'left');
				}
			});
			this.subscriptions.push(removeScanSubscription);
		}
	}

	getTotalScanned(): number {
		if (!this.currentItem || !this.currentItem.stockid || !Array.isArray(this.scan_history)) {
			return 0;
		}

		const stockid = this.currentItem.stockid;
		const total = this.scan_history.reduce((accumulator: number, entry: any) => {
			let addon = 0;
			if (entry.stockid === stockid) {
				addon = this.sanitizeNumber(entry.qty);
			}
			return accumulator + addon;
		}, 0);

		return total;
	}

	getTotalScannedForItem(stockid: string): number {
		if (!stockid || !Array.isArray(this.scan_history)) {
			return 0;
		}

		const total = this.scan_history.reduce((accumulator: number, entry: any) => {
			let addon = 0;
			if (entry.stockid === stockid) {
				addon = this.sanitizeNumber(entry.qty);
			}
			return accumulator + addon;
		}, 0);

		return total;
	}

	selectScanHistoryItem(scan: any): void {
		// Select the scan item and load its details
		this.currentItem = {
			stockid: scan.stockid,
			description: scan.description || 'Item Description',
			openpos: scan.qty || 0
		};

		this.itemDetailsForScaninout = {
			item: this.currentItem,
			qtyRec: scan.qty || 0,
			qtyBin: 'RECDOC'
		};

		// Load data for the selected item
		if (scan.stockid) {
			this.loadData(scan.stockid);
		}

		// Close scan history
		this.showScanHistory = false;

		// Show success feedback
		this.scanStatus = 'success';
		this.statusMessage = `Selected: ${scan.stockid}`;
		this.playSuccessSound();

		// Clear status after 2 seconds
		setTimeout(() => {
			this.scanStatus = 'idle';
			this.statusMessage = '';
		}, 2000);
	}


	incrementScanned(scan: any): void {
		const item: any = {
			quantityrecd: 1,
			stockid: scan.stockid,
			loccode: this.location,
			bin: scan.bin || this.qtyBin.value,
			vendor: scan.vendor || this.vendor_selected,
			sending: this.sendToggle.value ? 1 : 0
		};

		if (item.stockid === this.MST_ITEM_TYPE) {
			item['orderno'] = this.mstdetails?.orderno;
		}

		const data = { item, user: this.user.user };

		const incrementSubscription = this.inventoryService.addRecItem(data).subscribe({
			next: (response) => {
				this.sendUpdateRec(data);

				// Update RGA if in scan-out mode
				if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
					let rgaOrderno: string | null = null;

					// Check itemRgaMap first (for backward compatibility)
					if (this.itemRgaMap[scan.stockid]) {
						rgaOrderno = this.itemRgaMap[scan.stockid];
					} else if (this.vendorRgaMap[scan.vendor || this.vendor_selected]) {
						// Check vendorRgaMap as fallback
						rgaOrderno = this.vendorRgaMap[scan.vendor || this.vendor_selected];
					}

					if (rgaOrderno) {
						// RGA exists in map - update it
						const updateData = {
							orderno: rgaOrderno,
							stockid: scan.stockid,
							quantity: 1,
							action: 'add',
							description: scan.description || ''
						};
						this.purchasingService.updateRgaItem(updateData).subscribe({
							next: () => {
								// Update maps after successful update
								this.itemRgaMap[scan.stockid] = rgaOrderno;
								this.vendorRgaMap[scan.vendor || this.vendor_selected] = rgaOrderno;
							},
							error: (error) => console.error('Error updating RGA:', error)
						});
					} else {
						// Not in map - check/create RGA
						this.createOrUpdateRgaForItem(scan, 1).catch(error => {
							console.error('Error creating/updating RGA:', error);
						});
					}
				}

				// Add a small delay to ensure the backend has processed the update
				const timeoutId = setTimeout(() => {
					this.refreshScanHistory();
				}, 100);
				this.timeouts.push(timeoutId);
				this.globalSearchService.showNotification('Quantity incremented', 'success', 'top', 'left');
			},
			error: (error) => {
				this.globalSearchService.showNotification('Failed to increment', 'danger', 'top', 'left');
			}
		});
		this.subscriptions.push(incrementSubscription);
	}

	decrementScanned(scan: any): void {
		const item: any = {
			quantityrecd: -1,
			stockid: scan.stockid,
			loccode: this.location,
			bin: scan.bin || this.qtyBin.value,
			vendor: scan.vendor || this.vendor_selected,
			sending: this.sendToggle.value ? 1 : 0
		};

		if (item.stockid === this.MST_ITEM_TYPE) {
			item['orderno'] = this.mstdetails?.orderno;
		}

		const data = { item, user: this.user.user };

		const decrementSubscription = this.inventoryService.addRecItem(data).subscribe({
			next: (response) => {
				this.sendUpdateRec(data);

				// Update RGA if in scan-out mode
				if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
					let rgaOrderno: string | null = null;

					// Check itemRgaMap first (for backward compatibility)
					if (this.itemRgaMap[scan.stockid]) {
						rgaOrderno = this.itemRgaMap[scan.stockid];
					} else if (this.vendorRgaMap[scan.vendor || this.vendor_selected]) {
						// Check vendorRgaMap as fallback
						rgaOrderno = this.vendorRgaMap[scan.vendor || this.vendor_selected];
					}

					if (rgaOrderno) {
						// RGA exists in map - decrease quantity
						const updateData = {
							orderno: rgaOrderno,
							stockid: scan.stockid,
							quantity: -1,
							action: 'add',
							description: scan.description || ''
						};
						this.purchasingService.updateRgaItem(updateData).subscribe({
							next: () => {
								// Update maps after successful update
								this.itemRgaMap[scan.stockid] = rgaOrderno;
								this.vendorRgaMap[scan.vendor || this.vendor_selected] = rgaOrderno;
							},
							error: (error) => console.error('Error updating RGA:', error)
						});
					} else {
						// Not in map - check for existing RGA by vendor (not item)
						const vendorSearch = {
							vendor: scan.vendor || this.vendor_selected
							// Note: No stockid - this finds ANY open RGA for the vendor
						};
						this.purchasingService.getRgasForItemByVendor(vendorSearch).subscribe({
							next: (existingRgas: any) => {
								if (existingRgas && existingRgas.details && existingRgas.details.length > 0) {
									const rgaLine = existingRgas.details[0];
									rgaOrderno = rgaLine.orderno;
									// Update both maps
									this.itemRgaMap[scan.stockid] = rgaOrderno;
									this.vendorRgaMap[scan.vendor || this.vendor_selected] = rgaOrderno;

									const updateData = {
										orderno: rgaOrderno,
										stockid: scan.stockid,
										quantity: -1,
										action: 'add',
										description: scan.description || ''
									};
									this.purchasingService.updateRgaItem(updateData).subscribe({
										next: () => {},
										error: (error) => console.error('Error updating RGA:', error)
									});
								}
							},
							error: (error) => console.error('Error checking for RGA:', error)
						});
					}
				}

				// Add a small delay to ensure the backend has processed the update
				const timeoutId = setTimeout(() => {
					this.refreshScanHistory();
				}, 100);
				this.timeouts.push(timeoutId);
				this.globalSearchService.showNotification('Quantity decremented', 'success', 'top', 'left');
			},
			error: (error) => {
				this.globalSearchService.showNotification('Failed to decrement', 'danger', 'top', 'left');
			}
		});
		this.subscriptions.push(decrementSubscription);
	}

	private sanitizeNumber(x: any): number {
		if (Number.isNaN(x)) {
			x = 0;
		}
		return parseFloat(Number.parseFloat(x).toFixed(0));
	}

	// VENDOR MANAGEMENT

	loadVendors(): void {
		const loadVendorsSubscription = this.vendorService.getVendors().subscribe((vendors: any) => {
			this.availableVendors = vendors.filter((v: any) => v.bankref !== '');
			// No default vendor selection - user must choose
			this.selectedVendor = null;
			this.vendor_selected = 'DEFAULT';
		});
		this.subscriptions.push(loadVendorsSubscription);
	}

	onVendorChange(event: any): void {
		this.selectedVendor = event.value;
		this.vendor_selected = event.value.supplierid;
		this.showVendorButtons = false;

		// Clear selected PO when vendor changes
		this.selectedPoDetails = null;

		// Clear vendor RGA map when vendor changes
		// Keep itemRgaMap for items that might be scanned again
		delete this.vendorRgaMap[this.vendor_selected]; // Clear old vendor if exists

		// Focus on scanner input after vendor selection
		this.focusScannerInput();

		// Reload data if we have a current item
		if (this.currentItem && this.currentItem.stockid) {
			this.loadData(this.currentItem.stockid, false); // Just context change, no scan
		}
	}

	toggleVendorButtons(): void {
		this.showVendorButtons = !this.showVendorButtons;
	}

	// PURCHASE ORDER INTEGRATION

	private extractPODataFromItem(): void {
		if (!this.currentItem) {return;}

		// Extract PO data from the item's podata array OR from scan history fillpos
		const podata = this.currentItem.podata || [];
		const openpos = this.currentItem.openpos || 0;

		// Calculate totals from podata
		let totalOrdered = 0;
		let totalReceived = 0;
		let totalPending = 0;
		const details: any[] = [];

		// First, try to extract from podata array
		podata.forEach((po: any) => {
			const ordered = parseInt(po.ordered) || 0;
			const received = parseInt(po.rec) || 0;
			const pending = parseInt(po.pendingitems) || 0;

			totalOrdered += ordered;
			totalReceived += received;
			totalPending += pending;

			details.push({
				itemcode: this.currentItem?.stockid || '',
				itemdescription: this.currentItem?.description || '',
				orderno: po.orderno,
				quantityord: ordered,
				quantityrecd: received,
				remaining: pending
			});
		});

		// If no podata, try to extract from scan history fillpos
		if (details.length === 0 && Array.isArray(this.scan_history)) {
			const currentStockid = this.currentItem.stockid;

			// Find scan history entries for this item
			const relevantScans = this.scan_history.filter((scan: any) => scan.stockid === currentStockid);

			// Extract unique PO information from fillpos arrays
			const poMap = new Map();

			relevantScans.forEach((scan: any) => {
				if (scan.fillpos && Array.isArray(scan.fillpos)) {
					scan.fillpos.forEach((po: any) => {
						if (po.orderno && !poMap.has(po.orderno)) {
							const ordered = parseInt(po.quantityord) || parseInt(po.originalordqty) || 0;
							const received = parseInt(po.quantityrecd) || 0;
							const pending = ordered - received;

							totalOrdered += ordered;
							totalReceived += received;
							totalPending += pending;

							poMap.set(po.orderno, {
								itemcode: po.itemcode || currentStockid,
								itemdescription: po.itemdescription || this.currentItem?.description || '',
								orderno: po.orderno,
								quantityord: ordered,
								quantityrecd: received,
								remaining: pending,
								unitprice: po.unitprice,
								deliverydate: po.deliverydate,
								comments: po.header.comments
							});
						}
					});
				}

				// Also use the scan's qop (quantity on PO) if no fillpos
				if (!scan.fillpos && scan.qop) {
					totalOrdered = Math.max(totalOrdered, parseInt(scan.qop) || 0);
				}
			});

			details.push(...Array.from(poMap.values()));
		}

		// Create the structure that warehouse-scaninout expects
		this.purchaseOrders = {
			total_pending: totalPending,
			original_inbound: totalOrdered,
			total_received: totalReceived,
			details: details
		};

		this.expectedQuantity = totalPending;

		// Check for PO selection if feature is enabled
		this.checkAndShowPoSelection();
	}

	loadPurchaseOrders(stockid: string): void {
		if (!this.vendor_selected || this.vendor_selected === 'DEFAULT') {
			return;
		}

		this.loadingPurchaseOrders = true;

		if (stockid === this.MST_ITEM_TYPE) {
			// Handle MST items - need orderno from mstdetails
			if (!this.mstdetails?.orderno) {
				this.loadingPurchaseOrders = false;
				this.purchaseOrders = { total_pending: 0, original_inbound: 0, details: [] };
				return;
			}

			const data = {
				orderno: this.mstdetails.orderno
			};

			const mstPoSubscription = this.purchasingService.getMstPo(data).subscribe({
				next: (results: any) => {
					// MST PO data structure might be different
					this.purchaseOrders = results || { total_pending: 0, original_inbound: 0, details: [] };
					this.expectedQuantity = results?.[0]?.quantityord || 0;
					this.loadingPurchaseOrders = false;

					// Update receiving quantity based on PO data
					this.updateReceivingQuantityFromPO();

					// Check for PO selection if feature is enabled (MST items typically have single PO, but check anyway)
					this.checkAndShowPoSelection();
				},
				error: (error) => {
					this.loadingPurchaseOrders = false;
					this.purchaseOrders = { total_pending: 0, original_inbound: 0, details: [] };
					this.expectedQuantity = 0;
				}
			});
			this.subscriptions.push(mstPoSubscription);
		} else {
			// Handle regular items
			const search = {
				stockid: stockid,
				vendor: this.vendor_selected
			};

			const purchasesSubscription = this.purchasingService.getPurchasesForItemByVendor(search).subscribe({
				next: (results: any) => {
					// Ensure the data structure matches what warehouse-scaninout expects
					this.purchaseOrders = results || { total_pending: 0, original_inbound: 0, details: [] };
					this.expectedQuantity = results?.total_pending || 0;
					this.loadingPurchaseOrders = false;

					// Update receiving quantity based on PO data
					this.updateReceivingQuantityFromPO();

					// Check for PO selection if feature is enabled
					this.checkAndShowPoSelection();
				},
				error: (error) => {
					this.loadingPurchaseOrders = false;
					this.purchaseOrders = { total_pending: 0, original_inbound: 0, details: [] };
					this.expectedQuantity = 0;
				}
			});
			this.subscriptions.push(purchasesSubscription);
		}
	}

	private updateReceivingQuantityFromPO(): void {
		if (this.currentItem && this.expectedQuantity > 0) {
			const alreadyScanned = this.getTotalScanned();
			this.receiving_quantity = Math.max(0, this.expectedQuantity - alreadyScanned);
			this.qtyRec.setValue(this.receiving_quantity);
			// Don't auto-set bulk quantity input - let manager enter their own quantity
			// this.bulkQuantityInput = Math.max(0, this.receiving_quantity);
		}
	}

	getPurchaseOrderSummary(): string {
		if (!this.purchaseOrders || (Array.isArray(this.purchaseOrders) && this.purchaseOrders.length === 0)) {
			return 'No purchase orders found';
		}

		const totalPending = (this.purchaseOrders as any)?.total_pending || 0;
		const originalInbound = (this.purchaseOrders as any)?.original_inbound || 0;

		return `Expected: ${totalPending} | Original: ${originalInbound}`;
	}

	getPurchaseOrderNumbers(): string[] {
		const poNumbers: string[] = [];

		if (this.purchaseOrders?.details && Array.isArray(this.purchaseOrders.details)) {
			this.purchaseOrders.details.forEach((po: any) => {
				if (po.orderno) {
					poNumbers.push(po.orderno);
				}
			});
		}

		return poNumbers;
	}

	getOpenPOsArray(): any[] {
		// Handle both array and number formats for backward compatibility
		if (this.itemDetailsForScaninout?.openpos) {
			if (Array.isArray(this.itemDetailsForScaninout.openpos)) {
				return this.itemDetailsForScaninout.openpos;
			} else if (typeof this.itemDetailsForScaninout.openpos === 'number') {
				// If it's a number, return an empty array (legacy format)
				return [];
			}
		}
		return [];
	}

	getTotalOpenPOQuantity(): number {
		if (this.itemDetailsForScaninout?.openpos) {
			if (Array.isArray(this.itemDetailsForScaninout.openpos)) {
				return this.itemDetailsForScaninout.openpos.reduce((sum: number, po: any) => {
					return sum + (parseInt(po.quantityord) || 0);
				}, 0);
			} else if (typeof this.itemDetailsForScaninout.openpos === 'number') {
				return this.itemDetailsForScaninout.openpos;
			}
		}
		return 0;
	}

	// PURCHASE HISTORY LOADING

	loadPurchaseHistory(stockid: string): void {
		this.historyloading = true;
		const purchaseHistorySubscription = this.inventoryService.getPurchaseHistory(stockid).subscribe({
			next: (result: any) => {
				this.historyloading = false;
				this.history = result;
			},
			error: (error) => {
				this.historyloading = false;
				this.history = null;
			}
		});
		this.subscriptions.push(purchaseHistorySubscription);
	}

	// RGA DATA LOADING

	loadRgaData(stockid: string): void {
		if (!this.vendor_selected || this.vendor_selected === 'DEFAULT') {
			return;
		}

		const search = {
			stockid: stockid,
			vendor: this.vendor_selected
		};

		const rgaSubscription = this.purchasingService.getRgasForItemByVendor(search).subscribe({
			next: (results: any) => {
				// Ensure the data structure matches what warehouse-scaninout expects
				this.vendorRgas = results || { total_pending: 0, original_inbound: 0, details: [] };
			},
			error: (error) => {
				this.vendorRgas = { total_pending: 0, original_inbound: 0, details: [] };
			}
		});
		this.subscriptions.push(rgaSubscription);
	}

	// GET ITEM DESTINATION

	private getItemDestination(stockid: string, playAudio = true): void {
		const data = {
			stockid: stockid,
			loccode: this.location,
			vendor: this.vendor_selected
		};

		const itemDestinationSubscription = this.inventoryService.getItemDestination(data).subscribe({
			next: (result: any) => {
				if (result && this.currentItem) {
					// Store original destination
					this.currentItem.destination = result.destination || 'stock';

					// Use the centralized audio destination logic
					const scannedQty = parseInt(result.scannedQty) || this.getTotalScanned() || 0;
					const expectedQty = parseInt(result.expectedQty) || parseInt(result.stats?.openpos) || 0;

					// Use server's adjusted scanned quantity if available, otherwise calculate locally
					const adjustedScannedQty = result.adjustedScannedQty ||
						((this.scanMode === 'single') ? scannedQty + 1 : scannedQty);
					const audioDestination = this.determineAudioDestination(result, adjustedScannedQty, expectedQty);

					// Only play audio if:
					// 1. playAudio flag is true (not a mode change or refresh)
					// 2. We're in scan in mode (sendToggle.value === false)
					if (playAudio && !this.sendToggle.value) {
						this.playDestinationAudio(audioDestination);
					}

					// Update stats information if available
					if (result.stats) {
						this.currentItem.stats = result.stats;
						// Update openpos from stats if available
						if (result.stats.openpos) {
							this.currentItem.openpos = result.stats.openpos;
						}
						// Update reserved from stats if available
						if (result.stats.reserved) {
							this.currentItem.reserved = parseInt(result.stats.reserved) || 0;
						}
						// Update itemdemand from stats if available (needed for destination calculation)
						if (result.stats.itemdemand !== undefined) {
							this.currentItem.itemdemand = parseFloat(result.stats.itemdemand) || 0;
						}
						// Update total_qoh from stats if available
						if (result.stats.total_qoh !== undefined) {
							this.currentItem.total_qoh = parseFloat(result.stats.total_qoh) || 0;
						}
					}

					if (result.bin) {
						this.currentItem.receiving_bin = result.bin;
						this.receiving_bin = result.bin;
						this.qtyBin.enable();
						this.qtyBin.setValue(result.bin);
					} else {
						this.currentItem.receiving_bin = ({
							sticker: 'STICKER',
							staging: 'STAGING'
						}[result.destination]) || this.receiving_bin || 'RECDOC';
					}

					// Load full item details in background for complete information
					this.loadFullItemDetailsInBackground(stockid);
				}
			},
			error: (error) => {
				// Default to stock if destination lookup fails
				if (this.currentItem) {
					this.currentItem.destination = 'stock';
					this.currentItem.receiving_bin = this.receiving_bin || 'RECDOC';
				}
			}
		});
		this.subscriptions.push(itemDestinationSubscription);
	}

	// BACKGROUND LOADING OF FULL ITEM DETAILS

	private loadFullItemDetailsInBackground(stockid: string): void {
		// Load full item details from inventory service in background
		const fullItemDetailsSubscription = this.inventoryService.loadItem(stockid).subscribe({
			next: (fullItemDetails: any) => {
				if (fullItemDetails && this.currentItem) {
					// Merge the full item details with current item data
					this.currentItem = {
						...this.currentItem,
						...fullItemDetails,
						// Preserve the destination and stats from getRecDestination
						destination: this.currentItem.destination,
						stats: this.currentItem.stats,
						openpos: this.currentItem.openpos,
						reserved: this.currentItem.reserved,
						receiving_bin: this.currentItem.receiving_bin
					};

					// Update the item details for scaninout with complete data
					this.prepareItemDetailsForScaninout();
				}
			},
			error: (error) => {
				console.warn('Failed to load full item details in background:', error);
				// Don't show error to user since this is background loading
			}
		});
		this.subscriptions.push(fullItemDetailsSubscription);
	}

	// ITEM DETAILS FOR WAREHOUSE-SCANINOUT

	private prepareItemDetailsForScaninout(): void {
		if (!this.currentItem) {
			this.itemDetailsForScaninout = null;
			return;
		}

		// Ensure minmax has the correct structure
		const minmax = this.currentItem.minmax || [];
		const minmaxWithDefaults = minmax.length > 0 ? minmax : [{
			minimum: 0,
			maximum: 0,
			reorderlevel: 0
		}];

		// Ensure bins has the correct structure
		const bins = this.currentItem.bins || {};
		const binsWithDefaults = Object.keys(bins).length > 0 ? bins : {
			'01': [{
				loccode: '01',
				bin: '',
				defaultbin: '0',
				locationname: 'DEFAULT',
				quantity: '0',
				reorderlevel: '0',
				managed: '0',
				canupd: '1'
			}]
		};

		// Extract openpos details from scan history if available
		let openposDetails: any[] = [];
		if (Array.isArray(this.scan_history)) {
			const currentStockid = this.currentItem.stockid;
			const relevantScans = this.scan_history.filter((scan: any) => scan.stockid === currentStockid);

			// Extract PO details from fillpos
			const poMap = new Map();
			relevantScans.forEach((scan: any) => {
				if (scan.fillpos && Array.isArray(scan.fillpos)) {
					scan.fillpos.forEach((po: any) => {
						if (po.orderno && !poMap.has(po.orderno)) {
							poMap.set(po.orderno, {
								orderno: po.orderno,
								quantityord: parseInt(po.quantityord) || parseInt(po.originalordqty) || 0,
								quantityrecd: parseInt(po.quantityrecd) || 0,
								unitprice: po.unitprice,
								deliverydate: po.deliverydate,
								itemdescription: po.itemdescription
							});
						}
					});
				}
			});

			openposDetails = Array.from(poMap.values());
		}

		// Use purchaseOrders.details if we have them, otherwise use extracted openposDetails
		const finalOpenpos = this.purchaseOrders?.details || openposDetails;

		// Format data to match what warehouse-scaninout expects
		this.itemDetailsForScaninout = {
			item: {
				...this.currentItem,
				minmax: minmaxWithDefaults,
				bins: binsWithDefaults
			},
			total_qoh: this.currentItem.total_qoh || 0,
			openpos: finalOpenpos.length > 0 ? finalOpenpos : (this.currentItem.openpos || 0),
			reserved: this.currentItem.reserved || 0,
			itemdemand: this.currentItem.itemdemand || 0, // Total demand (getItemDemand) - needed for destination calculation
			reserved_orders: this.currentItem.reserved_orders || [],
			bins: binsWithDefaults,
			// Add other required fields that warehouse-scaninout might need
			destination: this.currentItem.destination || 'stock',
			stagingamt: this.currentItem.stagingamt || 0,
			stockamt: this.currentItem.stockamt || 0
		};
	}


	// FOCUS SEARCH HANDLER

	onFocusSearch(event: any): void {
		// Handle focus search event from warehouse-scaninout
		if (event.searching) {
			this.clearCurrentItem();
		}
	}

	// MODE CHANGE HA
	// UNDO LAST SCAN

	undoLastScan(): void {
		if (!this.scan_history || this.scan_history.length === 0) {return;}

		const lastScan = this.scan_history[0]; // Most recent scan
		this.undoingLast = true;

		// Send negative quantity to reverse the last scan
		const item: any = {
			quantityrecd: -1, // Always undo just 1 item
			stockid: lastScan.stockid,
			loccode: this.location,
			bin: lastScan.bin || this.qtyBin.value,
			vendor: lastScan.vendor || this.vendor_selected,
			sending: this.sendToggle.value ? 1 : 0
		};

		if (item.stockid === this.MST_ITEM_TYPE) {
			item['orderno'] = this.mstdetails?.orderno;
		}

		const data = { item, user: this.user.user };

		const undoLastScanSubscription = this.inventoryService.addRecItem(data).subscribe({
			next: (response) => {
				this.scanStatus = 'warning';
				this.statusMessage = `Undid ${lastScan.qty} ${lastScan.stockid}`;
				this.playWarningSound();
				this.vibrateDevice();

				// Send OMS update for the reversal
				this.sendUpdateRec(data);

				// Update RGA if in scan-out mode - decrease quantity by the amount undone
				if (this.sendToggle.value === true && this.selectedVendor && this.vendor_selected !== 'DEFAULT') {
					const quantityToDecrease = lastScan.qty || 1;
					let rgaOrderno: string | null = null;

					// Check itemRgaMap first (for backward compatibility)
					if (this.itemRgaMap[lastScan.stockid]) {
						rgaOrderno = this.itemRgaMap[lastScan.stockid];
					} else if (this.vendorRgaMap[lastScan.vendor || this.vendor_selected]) {
						// Check vendorRgaMap as fallback
						rgaOrderno = this.vendorRgaMap[lastScan.vendor || this.vendor_selected];
					}

					if (rgaOrderno) {
						// RGA exists in map - decrease quantity
						const updateData = {
							orderno: rgaOrderno,
							stockid: lastScan.stockid,
							quantity: -quantityToDecrease,
							action: 'add',
							description: lastScan.description || ''
						};
						this.purchasingService.updateRgaItem(updateData).subscribe({
							next: () => {
								// Update maps after successful update
								this.itemRgaMap[lastScan.stockid] = rgaOrderno;
								this.vendorRgaMap[lastScan.vendor || this.vendor_selected] = rgaOrderno;
							},
							error: (error) => console.error('Error updating RGA:', error)
						});
					} else {
						// Not in map - check for existing RGA by vendor (not item)
						const vendorSearch = {
							vendor: lastScan.vendor || this.vendor_selected
							// Note: No stockid - this finds ANY open RGA for the vendor
						};
						this.purchasingService.getRgasForItemByVendor(vendorSearch).subscribe({
							next: (existingRgas: any) => {
								if (existingRgas && existingRgas.details && existingRgas.details.length > 0) {
									const rgaLine = existingRgas.details[0];
									rgaOrderno = rgaLine.orderno;
									// Update both maps
									this.itemRgaMap[lastScan.stockid] = rgaOrderno;
									this.vendorRgaMap[lastScan.vendor || this.vendor_selected] = rgaOrderno;

									const updateData = {
										orderno: rgaOrderno,
										stockid: lastScan.stockid,
										quantity: -quantityToDecrease,
										action: 'add',
										description: lastScan.description || ''
									};
									this.purchasingService.updateRgaItem(updateData).subscribe({
										next: () => {},
										error: (error) => console.error('Error updating RGA:', error)
									});
								}
							},
							error: (error) => console.error('Error checking for RGA:', error)
						});
					}
				}

				// Refresh scan history to show the reversal
				this.refreshScanHistory();

				const timeoutId = setTimeout(() => {
					this.scanStatus = 'idle';
					this.statusMessage = '';
					this.undoingLast = false;
				}, 2000);
				this.timeouts.push(timeoutId);
			},
			error: (error) => {
				this.scanStatus = 'error';
				this.statusMessage = 'Failed to undo scan';
				this.undoingLast = false;
			}
		});
		this.subscriptions.push(undoLastScanSubscription);
	}

	// MISSING METHODS FROM WAREHOUSE-SCANINOUT

	getTotalScannedInSplit(item: any, bin: string): number {
		if (!item?.stockid || !Array.isArray(this.scan_history)) { return 0; }

		const stockid = item.stockid;
		const parent = this;

		const totalScan = this.scan_history.reduce(function(accumulator, entry) {
			let addon = 0;
			if (entry.stockid === stockid && entry.bin === bin) {
				addon = parent.sanitizeNumber(entry.qty);
			}
			return accumulator + addon;
		}, 0);

		return totalScan;
	}

	getPartName(scan: any): string {
		// Return stockid - description if available, otherwise just stockid
		if (scan.description) {
			return `${scan.stockid} - ${scan.description}`;
		} else if (scan.itemdescription) {
			return `${scan.stockid} - ${scan.itemdescription}`;
		} else if (this.currentItem && this.currentItem.stockid === scan.stockid && this.currentItem.description) {
			// If scan doesn't have description but current item does, use it
			return `${scan.stockid} - ${this.currentItem.description}`;
		} else if (this.itemDetailsForScaninout && this.itemDetailsForScaninout.item &&
			this.itemDetailsForScaninout.item.stockid === scan.stockid &&
			this.itemDetailsForScaninout.item.description) {
			// If item details are available, use that description
			return `${scan.stockid} - ${this.itemDetailsForScaninout.item.description}`;
		} else {
			return scan.stockid;
		}
	}

	getCurrentItemName(): string {
		// Return current item's full name (stockid - description)
		if (this.currentItem) {
			if (this.currentItem.description) {
				return `${this.currentItem.stockid} - ${this.currentItem.description}`;
			} else {
				return this.currentItem.stockid;
			}
		}
		return '';
	}

	updateBin(value: any): void {
		this.receiving_bin = value;
	}

	updateQty(value: any, event: any): void {
		if (value == '') {
			event.stopPropagation();
			event.preventDefault();
			return;
		}

		this.receiving_quantity = parseFloat(value);
		this.qtyRec.setValue(this.receiving_quantity);
	}

	loadOrder(order: any): void {
		// This would typically open a modal with order details
		// For now, just show a notification
		this.globalSearchService.showNotification(`Order ${order} details would open here`, 'info', 'top', 'left');
	}


	// BIN DESTINATION METHODS

	getBinDestinationClass(): string {
		if (!this.currentItem) {return 'bin-stock';}

		// Check multiple possible locations for destination/bin info
		let destination = this.currentItem.destination ||
			this.currentItem.$item_rec?.bin ||
			this.receiving_bin ||
			'stock';

		// Convert to lowercase for comparison
		destination = destination.toLowerCase();

		switch (destination) {
		case 'sticker':
			return 'bin-sticker';
		case 'staging':
			return 'bin-staging';
		case 'stock':
		default:
			return 'bin-stock';
		}
	}

	getBinDestinationLabel(): string {
		if (!this.currentItem) {return 'STOCK';}

		// Check multiple possible locations for destination/bin info
		let destination = this.currentItem.destination ||
			this.currentItem.$item_rec?.bin ||
			this.receiving_bin ||
			'stock';

		// Convert to lowercase for comparison
		destination = destination.toLowerCase();

		switch (destination) {
		case 'sticker':
			return 'STICKER';
		case 'staging':
			return 'STAGING';
		case 'stock':
		default:
			return 'STOCK';
		}
	}

	// KEYBOARD LISTENER CONTROL METHODS

	toggleKeyboardListener(): void {
		this.keyboardListenerEnabled = !this.keyboardListenerEnabled;
		this.globalSearchService.showNotification(
			`Keyboard listener ${this.keyboardListenerEnabled ? 'enabled' : 'disabled'}`,
			'info',
			'top',
			'left'
		);
	}

	enableKeyboardListener(): void {
		this.keyboardListenerEnabled = true;
	}

	disableKeyboardListener(): void {
		this.keyboardListenerEnabled = false;
	}

	// SEARCH FUNCTIONALITY METHODS

	toggleSearchMode(): void {
		this.isSearchMode = !this.isSearchMode;

		if (this.isSearchMode) {
			// Entering search mode - disable keyboard listener and clear scanner state
			this.disableKeyboardListener();
			this.clearCurrentItem();
			this.clearSearchResults();
			// Focus on search input after a short delay to ensure DOM is updated
			const timeoutId = setTimeout(() => {
				this.focusInlineSearchInput();
			}, 100);
			this.timeouts.push(timeoutId);
		} else {
			// Exiting search mode - enable keyboard listener
			this.enableKeyboardListener();
			this.clearSearchResults();
			// Blur any focused input to prevent keyboard popup
			this.blurAllInputs();
			// Focus scanner input to prepare for scanning
			setTimeout(() => {
				this.focusScannerInput();
			}, 100);
		}
	}

	// Mobile-specific search toggle
	onMobileScannerInputTap(): void {
		if (this.isMobile && !this.keyBuffer) {
			// Only toggle to search mode if we're on mobile and no barcode is being scanned
			this.toggleSearchMode();
		}
	}

	private focusSearchInput(): void {
		const searchInput = document.querySelector('.search-input-field input') as HTMLInputElement;
		if (searchInput) {
			searchInput.focus();
		}
	}

	private focusInlineSearchInput(): void {
		const searchInput = document.querySelector('.search-input-field') as HTMLInputElement;
		if (searchInput) {
			searchInput.focus();
		}
	}

	private focusScannerInput(): void {
		// Focus on the hidden scan input to prepare for barcode scanning
		if (this.hiddenScanInput) {
			this.hiddenScanInput.nativeElement.focus();
		}
	}

	private scrollToScanResult(): void {
		// Scroll to the scan result after a brief delay to ensure DOM is updated
		setTimeout(() => {
			// Try to find the scan result element (minimal scan card or bulk mode card)
			const scanResultElement = document.querySelector('.minimal-scan-card, .bulk-mode-card');

			if (scanResultElement) {
				scanResultElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest'
				});
			} else {
				// Fallback: scroll to the top of the card body
				const cardBody = document.querySelector('.card-body');
				if (cardBody) {
					cardBody.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'nearest'
					});
				}
			}
		}, 300); // Delay to allow DOM updates and animations to complete
	}

	private blurAllInputs(): void {
		const inputs = document.querySelectorAll('input, textarea');
		inputs.forEach((input: any) => {
			if (input !== document.activeElement) {
				input.blur();
			}
		});
	}

	performSearch(): void {
		if (!this.searchInput || this.searchInput.trim() === '') {
			this.globalSearchService.showNotification('Please enter a search term', 'warning', 'top', 'left');
			return;
		}

		// Check if vendor is selected before performing search
		if (!this.selectedVendor || this.vendor_selected === 'DEFAULT') {
			this.showVendorRequiredError();
			return;
		}

		this.isScanning = true;
		this.scanStatus = 'idle';
		this.statusMessage = 'Searching...';

		// Use the same search method as the scanner
		const searchData = { stockid: this.searchInput.trim() };

		const performSearchSubscription = this.inventoryService.itemSearch(searchData).subscribe({
			next: (results: any) => {
				this.handleSearchResults(results);
			},
			error: (error) => {
				this.handleSearchError(error);
			}
		});
		this.subscriptions.push(performSearchSubscription);
	}

	private handleSearchResults(results: any): void {
		this.isScanning = false;
		this.scanStatus = 'idle';
		this.statusMessage = '';

		if (results && results.length > 0) {
			// Remove duplicates by stockid to prevent showing the same item multiple times
			const uniqueResults = this.removeDuplicateResults(results);
			this.searchResults = uniqueResults;

			if (uniqueResults.length === 1) {
				// Auto-select single result with brief delay for user feedback
				this.scanStatus = 'success';
				this.statusMessage = 'Found 1 item, selecting...';

				// Brief delay to show the found item before auto-selecting
				const timeoutId = setTimeout(() => {
					this.showSearchResults = false;
					this.selectSearchResult(uniqueResults[0]);
					this.searchInput = ''; // Clear search input after auto-selection
					this.scanStatus = 'success';
					this.statusMessage = 'Item selected';
				}, 800);
				this.timeouts.push(timeoutId);
			} else {
				// Show multiple results
				this.showSearchResults = true;
				this.scanStatus = 'success';
				this.statusMessage = `Found ${uniqueResults.length} items`;
			}
		} else {
			// No search results found - show modal with add barcode option
			this.searchResults = [];
			this.scanStatus = 'warning';
			this.statusMessage = '🔍 NO ITEMS FOUND - Search returned no results';
			this.lastSearchTerm = this.searchInput;
			this.showBarcodeAddModal = true;
			this.showBarcodeAddForm = false;

			// Store original mode (search mode)
			this.originalMode = 'search';

			// Disable keyboard capture during barcode add process
			this.disableKeyboardListener();

			// Play different buzzer sound for no search results
			this.playItemNotFoundBuzzer();
		}
	}

	private handleSearchError(error: any): void {
		this.isScanning = false;
		this.scanStatus = 'error';
		this.statusMessage = 'Search error occurred';
		this.searchResults = [];
		this.showSearchResults = true;
	}

	/**
	 * Remove duplicate search results by stockid
	 * Prevents the same item from appearing multiple times in search results
	 */
	private removeDuplicateResults(results: any[]): any[] {
		if (!results || results.length === 0) {
			return results;
		}

		const seen = new Set<string>();
		const uniqueResults: any[] = [];

		for (const item of results) {
			const stockid = item.stockid ? item.stockid.toString().toUpperCase() : '';

			if (stockid && !seen.has(stockid)) {
				seen.add(stockid);
				uniqueResults.push(item);
			}
		}

		return uniqueResults;
	}

	selectSearchResult(item: any): void {
		// Set the selected item as current item
		this.currentItem = item;
		this.showSearchResults = false;
		this.searchInput = '';

		// Load data for this item (same as scanner)
		this.loadData(item.stockid);

		// AUTO-RECEIVE in Every Tire Mode (same behavior as scan)
		if (this.scanMode === 'single' && !this.sendToggle.value) {
			this.autoReceiveOne();
		} else if (this.scanMode === 'bulk' && this.sendToggle.value && this.bulkQuantity > 0) {
			this.recordBulkScan();
		} else {
			// In bulk mode, just display and wait for manager action
			this.scanStatus = 'success';
			this.statusMessage = `Found: ${item.description}`;
			this.binLocation = item.bin || 'Unknown';

			// Play success sound
			this.audioPlay();
		}

		// Switch back to scanner mode — but keep search mode in scan-out so qty input stays visible
		if (!this.sendToggle.value) {
			this.isSearchMode = false;
			this.enableKeyboardListener();
		}

		// Show success feedback
		this.scanStatus = 'success';
		this.statusMessage = `Selected: ${item.description}`;
		this.playSuccessSound();

		// Scroll to result after selecting from search
		this.scrollToScanResult();

		// Clear status after 2 seconds
		const timeoutId = setTimeout(() => {
			this.scanStatus = 'idle';
			this.statusMessage = '';
		}, 2000);
		this.timeouts.push(timeoutId);
	}

	clearSearchResults(): void {
		this.searchResults = [];
		this.showSearchResults = false;
		this.searchInput = '';
	}

	closeSearchResults(): void {
		this.showSearchResults = false;
	}

	// TOUCH GESTURE SUPPORT FOR BOTTOM SHEET

	onBottomSheetTouchStart(event: TouchEvent): void {
		if (!this.isMobile) {return;}

		this.touchStartY = event.touches[0].clientY;
		this.touchCurrentY = this.touchStartY;
		this.isDragging = true;
	}

	onBottomSheetTouchMove(event: TouchEvent): void {
		if (!this.isMobile || !this.isDragging) {return;}

		this.touchCurrentY = event.touches[0].clientY;
		const deltaY = this.touchCurrentY - this.touchStartY;

		// Only allow downward swipes
		if (deltaY > 0) {
			const bottomSheet = event.currentTarget as HTMLElement;
			const content = bottomSheet.querySelector('.search-results-sheet-content') as HTMLElement;
			if (content) {
				content.style.transform = `translateY(${Math.min(deltaY, 100)}px)`;
			}
		}
	}

	onBottomSheetTouchEnd(event: TouchEvent): void {
		if (!this.isMobile || !this.isDragging) {return;}

		this.isDragging = false;
		const deltaY = this.touchCurrentY - this.touchStartY;

		// If swiped down more than 100px, close the bottom sheet
		if (deltaY > 100) {
			this.closeSearchResults();
		} else {
			// Reset position
			const bottomSheet = event.currentTarget as HTMLElement;
			const content = bottomSheet.querySelector('.search-results-sheet-content') as HTMLElement;
			if (content) {
				content.style.transform = 'translateY(0)';
			}
		}
	}

	// LAST SCAN TIME DISPLAY

	getLastScanTimeDisplay(): string {
		if (!this.lastScanTime) {
			return 'No scans yet';
		}

		const now = Date.now();
		const diffMs = now - this.lastScanTime;
		const diffSeconds = Math.floor(diffMs / 1000);
		const diffMinutes = Math.floor(diffSeconds / 60);
		const diffHours = Math.floor(diffMinutes / 60);

		if (diffSeconds < 60) {
			return `${diffSeconds}s ago`;
		} else if (diffMinutes < 60) {
			return `${diffMinutes}m ago`;
		} else if (diffHours < 24) {
			return `${diffHours}h ago`;
		} else {
			const date = new Date(this.lastScanTime);
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
		}
	}

	private startTimeUpdateInterval(): void {
		// Clear existing interval
		if (this.timeUpdateInterval) {
			clearInterval(this.timeUpdateInterval);
			// Remove from tracking array
			const index = this.intervals.indexOf(this.timeUpdateInterval);
			if (index > -1) {
				this.intervals.splice(index, 1);
			}
		}

		// Start new interval to update display every second
		this.timeUpdateInterval = setInterval(() => {
			if (this.lastScanTime) {
				// Use setTimeout to defer change detection and avoid ExpressionChangedAfterItHasBeenCheckedError
				const timeoutId = setTimeout(() => {
					this.cdr.detectChanges();
				}, 0);
				this.timeouts.push(timeoutId);
			}
		}, 1000);

		// Track the interval
		this.intervals.push(this.timeUpdateInterval);
	}

	private stopTimeUpdateInterval(): void {
		if (this.timeUpdateInterval) {
			clearInterval(this.timeUpdateInterval);
			// Remove from tracking array
			const index = this.intervals.indexOf(this.timeUpdateInterval);
			if (index > -1) {
				this.intervals.splice(index, 1);
			}
			this.timeUpdateInterval = null;
		}
	}

	// MOBILE DETECTION

	private detectMobile(): void {
		// Check if device is mobile based on screen width and user agent
		this.isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

		// Listen for window resize to update mobile detection
		this.resizeListener = () => {
			this.isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
		};
		window.addEventListener('resize', this.resizeListener);
	}

	// STATE MANAGEMENT METHODS

	resetToScannerMode(): void {
		// Reset all search-related state
		this.isSearchMode = false;
		this.clearSearchResults();

		// Reset modal states
		this.showMstModal = false;
		this.mstList = [];

		// Reset vendor PO display
		this.showVendorPoTable = false;
		this.vendorPoDetails = [];

		// Enable keyboard listener
		this.enableKeyboardListener();

		// Clear any focused inputs
		this.blurAllInputs();

		this.globalSearchService.showNotification('Reset to Scanner Mode', 'info', 'top', 'left');
	}

	resetAllState(): void {
		// Clear current item and all related data
		this.clearCurrentItem();

		// Reset search state
		this.clearSearchResults();
		this.isSearchMode = false;

		// Reset modal states
		this.showMstModal = false;
		this.mstList = [];

		// Reset vendor PO display
		this.showVendorPoTable = false;
		this.vendorPoDetails = [];

		// Reset scan mode to single
		this.scanMode = 'single';

		// Reset bulk quantities
		this.bulkQuantity = 0;
		this.bulkQuantityInput = 0;


		// Enable keyboard listener
		this.enableKeyboardListener();

		// Clear any focused inputs
		this.blurAllInputs();

		// Reset scan status
		this.scanStatus = 'idle';
		this.statusMessage = '';

		this.globalSearchService.showNotification('All state reset', 'info', 'top', 'left');
	}

	// ADD BARCODE FUNCTIONALITY

	openBarcodeAddModal(): void {
		this.showBarcodeAddModal = true;
		this.lastSearchTerm = '';
		this.scanStatus = 'idle';
		this.statusMessage = 'Ready to scan';

		// Store original mode based on current state
		this.originalMode = this.isSearchMode ? 'search' : 'scan';

		// Disable keyboard capture during barcode add process
		this.disableKeyboardListener();
	}

	closeBarcodeAddModal(): void {
		this.showBarcodeAddModal = false;
		this.scanStatus = 'idle';
		this.statusMessage = 'Ready to scan';

		// Restore original mode
		switch (this.originalMode) {
		case 'scan':
			this.isSearchMode = false;
			this.enableKeyboardListener();
			break;

		case 'search':
			this.isSearchMode = true;
			this.disableKeyboardListener();
			break;
		}

		this.originalMode = null;

		// Refocus scanner input after modal closes
		this.refocusScannerAfterModalClose();
	}

	addedBarcode(selectedItem: any): void {
		this.showBarcodeAddModal = false;
		this.showBarcodeAddForm = false;
		this.scanStatus = 'success';
		this.statusMessage = 'Barcode added successfully!';

		// Restore original mode
		if (this.originalMode === 'scan') {
			this.isSearchMode = false;
			this.enableKeyboardListener();
		} else if (this.originalMode === 'search') {
			this.isSearchMode = true;
			this.disableKeyboardListener();
		}

		this.originalMode = null;

		// Brief delay to show success message, then trigger scan of the added barcode
		const timeoutId = setTimeout(() => {
			if (this.lastSearchTerm && this.lastSearchTerm.trim()) {
				// Trigger a scan of the barcode that was just added
				this.processScanInput(this.lastSearchTerm.trim());
			} else if (selectedItem && selectedItem.stockid) {
				// Fallback: if no lastSearchTerm, load the item that was selected
				this.loadData(selectedItem.stockid);
				this.scanStatus = 'success';
				this.statusMessage = `Barcode added to: ${selectedItem.stockid}`;
				this.playSuccessSound();
			}

			// Refocus scanner input after success
			this.refocusScannerAfterModalClose();
		}, 1500);
		this.timeouts.push(timeoutId);
	}

	dspBarcodeAdd(): void {
		// Store original mode before switching
		this.originalMode = this.isSearchMode ? 'search' : 'scan';

		// Switch to search mode to allow keyboard input
		this.isSearchMode = true;
		this.showBarcodeAddForm = true;
		this.barcodeAddError = '';
	}

	cancelBarcodeAdd(): void {
		this.showBarcodeAddForm = false;
		this.showBarcodeAddModal = false;
		this.barcodeAddError = '';
		this.scanStatus = 'idle';
		this.statusMessage = 'Ready to scan';

		// Restore original mode
		switch (this.originalMode) {
		case 'scan':
			this.isSearchMode = false;
			this.enableKeyboardListener();
			break;

		case 'search':
			this.isSearchMode = true;
			this.disableKeyboardListener();
			break;
		}

		this.originalMode = null;

		// Refocus scanner input after cancellation
		this.refocusScannerAfterModalClose();
	}


	// COORDINATED STATE UPDATES

	onItemSelected(item: any): void {
		// Clear all other states when an item is selected
		this.clearSearchResults();
		this.showMstModal = false;
		this.mstList = [];
		this.showVendorPoTable = false;
		this.vendorPoDetails = [];

		// Set the item and load data
		this.currentItem = item;
		this.loadData(item.stockid);

		// Switch back to scanner mode
		this.isSearchMode = false;
		this.enableKeyboardListener();
	}

	// MANAGER MODE TOGGLE

	get isManagerMode(): boolean {
		return this.scanMode === 'bulk';
	}

	toggleManagerMode(): void {

		// Scan-out always requires bulk mode (manual quantity per scan).
		// Don't let the user accidentally drop back to single-tire auto-receive while sending.
		if (this.sendToggle.value && this.scanMode === 'bulk') {
			return;
		}

		switch (this.scanMode) {
		case 'single':
			// Entering manager mode
			this.modeBeforeManager = this.isSearchMode; // Save current search mode state
			this.scanMode = 'bulk';
			this.isSearchMode = true; // Always enable search in manager mode
			this.disableKeyboardListener();
			break;

		case 'bulk':
			// Exiting manager mode
			this.scanMode = 'single';
			this.isSearchMode = this.modeBeforeManager; // Restore previous search mode state

			// Enable/disable keyboard listener based on restored mode
			if (this.isSearchMode) {
				this.disableKeyboardListener();
			} else {
				this.enableKeyboardListener();
			}
			break;
		}

		this.onModeChange();
	}

	// RECEIVE/SEND TOGGLE

	toggleReceiveSend(): void {
		this.sendToggle.setValue(!this.sendToggle.value);

		// Clear selected PO when switching modes
		this.selectedPoDetails = null;
	}

	onModeChange(): void {
		// Clear current item when switching modes
		this.clearCurrentItem();
		this.scanStatus = 'idle';
		this.statusMessage = '';

		// Reset all item-related data
		this.itemDetailsForScaninout = null;
		this.purchaseOrders = null;
		this.vendorRgas = null;
		this.history = null;
		this.showVendorPoTable = false;
		this.vendorPoDetails = [];

		// Clear search state
		this.clearSearchResults();

		// Only reset search mode if not in manager mode (manager mode manages its own search state)
		if (!this.isManagerMode) {
			this.isSearchMode = false;
			this.enableKeyboardListener();
		}

		// Mode-specific setup
		if (this.scanMode === 'bulk') {
			if (this.sendToggle.value) {
				this.statusMessage = 'Scan-Out: enter quantity, then scan item to record';
			} else {
				this.statusMessage = 'Manager Bulk Mode: Scan item to receive full PO quantity';
			}
		} else {
			// Every Tire Mode (default) - minimal info, auto-receive
			this.statusMessage = 'Every Tire Mode: Scan each tire individually';
		}
	}

	// MST MODAL METHODS

	openMstModal(): void {
		this.showMstModal = true;
		// Clear previous data and load fresh data
		this.mstList = [];
		this.loadMstData();
	}

	closeMstModal(): void {
		this.showMstModal = false;
		this.mstList = [];

		// If we were adding a barcode, reset that state too
	}

	preloadItem(item: any): void {
		const stockidsearch = item.stockid != undefined ? item.stockid : item.stkcode != undefined ? item.stkcode : item.itemcode;

		if (stockidsearch === 'MST') {
			// For MST items, create a mock item instead of loading from inventory service
			const mockMstItem = {
				stockid: 'MST',
				description: 'MST ',
				openpos: item.quantityord || item.quantity || 0,
				qoh: 0,
				minmax: { minimum: 0, maximum: 0 }
			};

			this.currentItem = mockMstItem;
			this.itemDetailsForScaninout = {
				item: mockMstItem,
				qtyRec: 0,
				qtyBin: 'RECDOC',
				openpos: item.quantityord || item.quantity || 0
			};

			// Set MST details for display
			this.mstdetails = item;

			// Load MST PO data
			const data = {
				orderno: item.orderno
			};
			const mstPoDataSubscription = this.purchasingService.getMstPo(data).subscribe(results => {
				this.purchaseOrders = results;
			});
			this.subscriptions.push(mstPoDataSubscription);

			// Load RGAs for MST
			const search = {
				stockid: 'MST',
				vendor: this.vendor_selected
			};
			const mstRgaSubscription = this.purchasingService.getRgasForItemByVendor(search).subscribe((results: any) => {
				this.vendorPoDetails = results || [];
			});
			this.subscriptions.push(mstRgaSubscription);

			// Close modal
			this.closeMstModal();

			// Show success feedback
			this.scanStatus = 'success';
			this.statusMessage = `Selected MST: ${item.orderno}`;
			this.playSuccessSound();

			// AUTO-RECEIVE in Every Tire Mode (same as handleScanResults).
			// Scan-out forces bulk and requires a typed quantity first.
			if (this.scanMode === 'single' && !this.sendToggle.value) {
				this.autoReceiveOne();
			} else if (this.scanMode === 'bulk' && this.sendToggle.value && this.bulkQuantity > 0) {
				this.recordBulkScan();
			}

			// Clear status after 2 seconds
			const timeoutId = setTimeout(() => {
				this.scanStatus = 'idle';
				this.statusMessage = '';
			}, 2000);
			this.timeouts.push(timeoutId);
		} else {
			// For regular items, load from inventory service
			const loadItemSubscription = this.inventoryService.loadItem(stockidsearch).subscribe((result: any) => {
				this.currentItem = result;
				this.itemDetailsForScaninout = {
					item: result,
					qtyRec: 0,
					qtyBin: 'RECDOC'
				};

				const search = {
					stockid: stockidsearch,
					vendor: this.vendor_selected
				};

				const loadItemPurchasesSubscription = this.purchasingService.getPurchasesForItemByVendor(search).subscribe(results => {
					this.purchaseOrders = results;
				});
				this.subscriptions.push(loadItemPurchasesSubscription);

				const loadItemRgaSubscription = this.purchasingService.getRgasForItemByVendor(search).subscribe((results: any) => {
					this.vendorPoDetails = results || [];
				});
				this.subscriptions.push(loadItemRgaSubscription);

				// Close modal
				this.closeMstModal();

				// Show success feedback
				this.scanStatus = 'success';
				this.statusMessage = `Selected item: ${result.description}`;
				this.playSuccessSound();

				// AUTO-RECEIVE in Every Tire Mode (same as handleScanResults).
				// Scan-out forces bulk and requires a typed quantity first.
				if (this.scanMode === 'single' && !this.sendToggle.value) {
					this.autoReceiveOne();
				} else if (this.scanMode === 'bulk' && this.sendToggle.value && this.bulkQuantity > 0) {
					this.recordBulkScan();
				}

				// Clear status after 2 seconds
				const timeoutId = setTimeout(() => {
					this.scanStatus = 'idle';
					this.statusMessage = '';
				}, 2000);
				this.timeouts.push(timeoutId);
			});
			this.subscriptions.push(loadItemSubscription);
		}
	}

	setSelectedMst(mst: any = ''): void {
		this.mstdetails = mst;
	}

	selectMstItem(mst: any): void {
		// Use the same flow as warehouse-item-scan-two
		this.preloadItem(mst);
		this.setSelectedMst(mst);
	}

	loadMstData(): void {
		// Set loading state
		this.loadingMstData = true;
		this.mstList = []; // Clear previous data

		if (this.mstToggle.value) {
			// Send mode - get sales orders
			const mstSalesOrdersSubscription = this.ordersService.getMstOnOpenSalesOrders().subscribe({
				next: (results: any) => {
					this.loadingMstData = false;
					this.mstList = results || [];
				},
				error: (error) => {
					this.loadingMstData = false;
					this.globalSearchService.showNotification('Failed to load MST sales orders', 'danger', 'top', 'left');
					this.closeMstModal();
				}
			});
			this.subscriptions.push(mstSalesOrdersSubscription);
		} else {
			// Receive mode - get purchase orders
			if (!this.vendor_selected || this.vendor_selected === 'DEFAULT') {
				this.globalSearchService.showNotification('Please select a vendor first', 'warning', 'top', 'left');
				this.closeMstModal();
				return;
			}

			const data = { vendor: this.vendor_selected };
			const mstPurchOrdersSubscription = this.ordersService.getMstOnOpenPurchOrders(data).subscribe({
				next: (results: any) => {
					this.loadingMstData = false;
					this.mstList = results || [];
				},
				error: (error) => {
					this.loadingMstData = false;
					this.globalSearchService.showNotification('Failed to load MST purchase orders', 'danger', 'top', 'left');
					this.closeMstModal();
				}
			});
			this.subscriptions.push(mstPurchOrdersSubscription);
		}
	}

	// VENDOR PO DISPLAY METHODS

	toggleVendorPoDisplay(): void {
		this.showVendorPoTable = !this.showVendorPoTable;

		if (this.showVendorPoTable && this.currentItem) {
			this.loadVendorPoDetails();
		}
	}

	loadVendorPoDetails(): void {
		if (!this.currentItem || !this.currentItem.stockid || !this.vendor_selected || this.vendor_selected === 'DEFAULT') {
			return;
		}

		const search = {
			stockid: this.currentItem.stockid,
			vendor: this.vendor_selected
		};

		const vendorPoDetailsSubscription = this.purchasingService.getPurchasesForItemByVendor(search).subscribe({
			next: (results: any) => {
				this.vendorPoDetails = results?.details || [];
			},
			error: (error) => {
				this.vendorPoDetails = [];
			}
		});
		this.subscriptions.push(vendorPoDetailsSubscription);
	}

	// Scan Detail Modal Methods
	showScanDetail(scan: any): void {
		this.selectedScanDetail = scan;
		this.showScanDetailModal = true;
	}

	closeScanDetail(): void {
		this.showScanDetailModal = false;
		this.selectedScanDetail = null;
	}

	getScanStatusClass(scan: any): string {
		if (scan.in_out == 0) {
			switch (true) {
			case scan.qty > scan.qop:
				return 'status-over';
			case scan.qty < scan.qop && scan.qty > 0:
				return 'status-partial';
			case scan.qty === scan.qop:
				return 'status-complete';
			default:
				return 'status-default';
			}
		} else {
			return 'status-outbound';
		}
	}

	getScanStatusText(scan: any): string {
		if (scan.in_out == 0) {
			switch (true) {
			case scan.qty > scan.qop:
				return 'Over Quantity';
			case scan.qty < scan.qop && scan.qty > 0:
				return 'Partial';
			case scan.qty === scan.qop:
				return 'Complete';
			default:
				return 'Unknown';
			}
		} else {
			return 'Outbound';
		}
	}

	// Refocus scanner input after modal closes
	private refocusScannerAfterModalClose(): void {
		// Use a longer delay to ensure modal animations complete
		const timeoutId = setTimeout(() => {
			if (this.isSearchMode) {
				// If in search mode, focus the search input
				this.focusInlineSearchInput();
			} else {
				// If in scan mode, focus the scanner input
				this.focusScannerInput();
			}
		}, 400); // Slightly longer delay to ensure modal is fully closed
		this.timeouts.push(timeoutId);
	}

	// SOCKET STATUS MONITORING

	private startSocketStatusMonitoring(): void {
		// Check socket status immediately
		this.updateSocketStatus();

		// Check socket status every 5 seconds
		const statusInterval = setInterval(() => {
			this.updateSocketStatus();
		}, 5000);
		this.intervals.push(statusInterval);
	}

	private updateSocketStatus(): void {
		if (this.omsService.socket) {
			const connectionState = this.omsService.getConnectionState();
			this.socketConnected = this.omsService.isSocketHealthy();

			switch (connectionState) {
			case 'connected':
				this.socketStatusMessage = 'Connected';
				break;
			case 'connecting':
				this.socketStatusMessage = 'Connecting...';
				break;
			case 'disconnected':
				this.socketStatusMessage = 'Disconnected';
				break;
			case 'error':
				this.socketStatusMessage = 'Connection Error';
				break;
			default:
				this.socketStatusMessage = 'Unknown';
			}
		} else {
			this.socketConnected = false;
			this.socketStatusMessage = 'No Socket';
		}
	}

	getSocketStatusClass(): string {
		if (this.socketConnected) {
			return 'socket-status-connected';
		} else {
			return 'socket-status-disconnected';
		}
	}

	getSocketStatusIcon(): string {
		if (this.socketConnected) {
			return 'wifi';
		} else {
			return 'wifi_off';
		}
	}

	// Allow manual reconnection
	reconnectSocket(): void {
		if (this.omsService) {
			this.omsService.forceReconnect();
			this.updateSocketStatus();
		}
	}

	// RGA CREATION METHODS

	async createOrUpdateRgaForItem(item: any, quantity: number): Promise<void> {
		if (!item || !item.stockid || quantity <= 0) {
			console.warn('createOrUpdateRgaForItem: Invalid item or quantity', { item, quantity });
			return;
		}

		if (!this.selectedVendor || this.vendor_selected === 'DEFAULT') {
			console.warn('createOrUpdateRgaForItem: No vendor selected. Cannot create RGA.', {
				selectedVendor: this.selectedVendor,
				vendor_selected: this.vendor_selected,
				stockid: item.stockid
			});
			return;
		}

		try {
			// Check if an RGA has already been created for this vendor during this session.
			let rgaOrderno: string | null = this.vendorRgaMap[this.vendor_selected] ? String(this.vendorRgaMap[this.vendor_selected]) : null;

			if (rgaOrderno) {
				// RGA already exists for this session, update it.
				const updateData = {
					orderno: rgaOrderno,
					stockid: item.stockid,
					quantity: quantity,
					action: 'set',
					description: item.description || item.itemdescription || ''
				};

				const result: any = await this.purchasingService.updateRgaItem(updateData).toPromise();
				if (!result || !result.success) {
					console.warn('Failed to update existing RGA.', { result, updateData });
				}
			} else {
				// No RGA exists for this vendor in this session, create a new one.
				const details = [{
					stockid: item.stockid,
					description: item.description || item.itemdescription || '',
					quantity: quantity,
					price: 0,
					line_notes: ''
				}];

				const supplier = this.selectedVendor;
				const location = this.user.user?.defaultlocation || {};
				const tomorrow = new Date();
				tomorrow.setDate(tomorrow.getDate() + 1);

				const header = {
					initiator: this.user.user?.userid || '',
					refrenceno: '',
					warehouse: this.location || location.loccode || '01',
					deladd1: location.deladd1 || '',
					deladd2: location.deladd2 || '',
					deladd3: location.deladd3 || '',
					deladd4: location.deladd4 || '',
					deladd5: location.deladd5 || '',
					deladd6: location.deladd6 || '',
					address1: supplier.address1 || '',
					address2: supplier.address2 || '',
					address3: supplier.address3 || '',
					address4: supplier.address4 || '',
					address5: supplier.address5 || '',
					address6: supplier.address6 || '',
					deliverydate: tomorrow.toISOString()
				};

				const result: any = await this.purchasingService.createRga(supplier, header, details, this.user).toPromise();
				if (result && result.success) {
					// A new RGA was created. Cache its order number for this vendor for the rest of the session.
					this.vendorRgaMap[this.vendor_selected] = result.orderno;
				} else {
					console.warn('RGA creation failed.', { result });
				}
			}
		} catch (error) {
			console.error('An unexpected error occurred in createOrUpdateRgaForItem:', error);
		}
	}

	// PO SELECTION METHODS

	getPoDisplayNumber(po: any): string {
		if (!po) {return '';}
		// Prioritize sales order number (what warehouse sees)
		if (po.sales_orderno && po.sales_orderno.trim() !== '') {
			// If multiple sales orders, take the first one (or could show all)
			const salesOrders = po.sales_orderno.split(',').map((s: string) => s.trim());
			return salesOrders[0];
		}
		// Fallback to PO number if no sales order linked
		return po.orderno;
	}

	checkAndShowPoSelection(): void {
		// Only check if feature is enabled and in scan-in mode
		if (!this.enablePoSelection || this.sendToggle.value === true) {
			return;
		}

		// Check if we have purchase orders with multiple POs
		if (this.purchaseOrders && this.purchaseOrders.details && Array.isArray(this.purchaseOrders.details)) {
			if (this.purchaseOrders.details.length > 1) {
				// Multiple POs - show modal
				this.openPoSelectionModal();
			} else if (this.purchaseOrders.details.length === 1) {
				// Single PO - auto-select it
				this.selectedPoDetails = this.purchaseOrders.details[0];
			}
		}
	}

	openPoSelectionModal(): void {
		if (this.purchaseOrders && this.purchaseOrders.details && Array.isArray(this.purchaseOrders.details)) {
			this.availablePos = this.purchaseOrders.details;
			this.showPoSelectionModal = true;
		}
	}

	closePoSelectionModal(): void {
		this.showPoSelectionModal = false;
	}

	selectPo(po: any): void {
		this.selectedPoDetails = po;
		this.closePoSelectionModal();

		const displayNumber = this.getPoDisplayNumber(po);
		this.scanStatus = 'success';
		this.statusMessage = `Selected PO: ${displayNumber}`;
		this.audioPlay();

		setTimeout(() => {
			this.scanStatus = 'idle';
			this.statusMessage = '';
		}, 2000);
	}

	onPoSelectionToggleChange(): void {
		// Clear selection when toggle is turned off
		if (!this.enablePoSelection) {
			this.selectedPoDetails = null;
		} else {
			// If toggle is turned on, check for PO selection
			this.checkAndShowPoSelection();
		}
	}
}